import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, StarOff, Lock, Sparkles, RefreshCcw, ShieldCheck, Info, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { TransparencyPanel } from "@/components/TransparencyPanel";
import StatusBadge from "@/components/StatusBadge";
import KpiCard from "@/components/KpiCard";
import { getKpiTone, kpiToneAccentClass } from "@/lib/kpiTone";
import ScanLoader from "@/components/ScanLoader";
import PriceChart from "@/components/PriceChart";
import GrowthChart from "@/components/GrowthChart";
import PaywallModal from "@/components/PaywallModal";
import ScorePanel from "@/components/ScorePanel";
import InstitutionalSnapshot from "@/components/InstitutionalSnapshot";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import KeyDriversPanel from "@/components/KeyDriversPanel";
import AlphaSignals from "@/components/AlphaSignals";
import UpgradePanel from "@/components/UpgradePanel";
import StockNotFound from "@/components/StockNotFound";
import AnonymousAnalyzeGate from "@/components/AnonymousAnalyzeGate";
import LiveDataChip from "@/components/LiveDataChip";
import ProvenancePopover from "@/components/ProvenancePopover";
import AnalyzeErrorBoundary from "@/components/AnalyzeErrorBoundary";
import SectionErrorBoundary from "@/components/SectionErrorBoundary";
import CompanyIdentityHeader from "@/components/analyze/CompanyIdentityHeader";
import CompanyProfileAccordion from "@/components/analyze/CompanyProfileAccordion";
import LockedPreviewCard from "@/components/analyze/LockedPreviewCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/lib/i18n";
import api from "@/lib/api";
import { API as RESOLVED_API_BASE } from "@/lib/api";
import { readTickerProvenance } from "@/lib/ticker_provenance";
import { emitLifecycle, newClientTraceId } from "@/lib/lifecycle";
import { hasAuthHint, clearAuthHint } from "@/lib/authHint";
import { pollAndApplyCacheBust } from "@/lib/cache_bust";
import {
  fmtCompact, fmtCurrency, fmtMoney, fmtMoneyCompact, fmtMultiplier, fmtPercent, fmtPrice, fmtRatio,
} from "@/lib/format";
import { toast } from "sonner";

function modeColor(mode) {
  if (mode === "Growth") return "text-[#3182CE] border-[rgba(49,130,206,0.35)] bg-[rgba(49,130,206,0.10)]";
  if (mode === "Speculative") return "text-[#A47864] border-[rgba(164,120,100,0.35)] bg-[rgba(164,120,100,0.10)]";
  return "text-[#A8AEB7] border-[rgba(168,174,183,0.35)] bg-[rgba(168,174,183,0.10)]";
}

/**
 * Phase 6.AK — TimeoutScreenTelemetry (enhanced)
 *
 * Posts a single `timeout_screen_displayed` event to
 * /api/_health/frontend_lifecycle the moment the timeout/error surface
 * mounts. Now also captures a FULL state snapshot so the operator can
 * tell — from one row — whether this timeout corresponds to a real
 * backend timeout, a transport error, an auth-gate bail, or a stale
 * state contamination.
 *
 * Renders nothing visible (returns null). One-shot useEffect; never re-fires.
 */
function TimeoutScreenTelemetry({
  ticker,
  reason,
  requestId,
  hasData,
  userAgent,
  clientTraceId,
  didFetchStart,
  didBackendRequestStart,
  routeTicker,
  typedTicker,
  previousTicker,
  authState,
}) {
  React.useEffect(() => {
    try {
      const eventName =
        reason === "timeout" ? "timeout_screen_displayed"
        : reason === "data_unavailable" ? "data_unavailable_displayed"
        : `error_screen_displayed_${reason || "unknown"}`;
      emitLifecycle(eventName, {
        ticker,
        request_id: requestId || null,
        user_agent: userAgent || null,
        client_trace_id: clientTraceId || null,
        extra: {
          reason,
          had_data_when_shown: !!hasData,
          // Phase 6.AK — full state snapshot. Especially critical:
          //   did_fetch_start = was `runAnalyze` even called?
          //   did_backend_request_start = did axios actually try to POST?
          // If BOTH are false but the timeout screen is showing, the
          // user is seeing a STALE state from a previous render that
          // never got reset.
          did_fetch_start: !!didFetchStart,
          did_backend_request_start: !!didBackendRequestStart,
          route_ticker: routeTicker || null,
          typed_ticker: typedTicker || null,
          previous_ticker: previousTicker || null,
          auth_state: authState || null,
          pathname: (typeof window !== "undefined") ? window.location.pathname : null,
        },
      });
      // Phase 6.AK — STAND-OUT detector. If a timeout/error screen
      // rendered but the analyze fetch never even started (no
      // runAnalyze call, no axios POST), the user is staring at
      // STALE state. Emit a separate high-severity event so the
      // Owner Console can show this as its own alert class — it's
      // a different bug pattern from "fetch started but never
      // reached backend".
      if (!didFetchStart) {
        emitLifecycle("timeout_screen_no_fetch_started", {
          ticker,
          client_trace_id: clientTraceId || null,
          extra: {
            reason,
            route_ticker: routeTicker || null,
            typed_ticker: typedTicker || null,
            previous_ticker: previousTicker || null,
            auth_state: authState || null,
            had_data_when_shown: !!hasData,
            pathname: (typeof window !== "undefined") ? window.location.pathname : null,
            diagnosis:
              authState === "loading" ? "auth_still_loading_state_stale"
              : authState === "unauthed" ? "no_user_session_state_stale"
              : "unknown_stale_state_no_fetch",
          },
        });
      }
    } catch { /* never break the UX */ }
    // Single-fire on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * AnalysisComponentMounted — Phase 6.AU mount sentinel.
 *
 * Rendered ONLY inside the main analyze layout (after every IIFE error
 * branch has been bypassed). Emits the `analysis_component_mounted`
 * lifecycle event from a useEffect on mount — meaning the analysis
 * JSX has actually committed to the DOM by the time this fires.
 *
 * This is intentionally a distinct signal from `render_completed`
 * (which is emitted from a setTimeout(0) in the success/recovery
 * branches and can race ahead of the actual commit) and from
 * `result_state_set` (which fires from a useEffect on `data` change
 * but BEFORE we know the analysis content has won the render).
 *
 * Operators correlating Owner Console logs should treat
 * `analysis_component_mounted` as the canonical "user is seeing the
 * analysis" proof. If `recovery_poll_hit` is present but
 * `analysis_component_mounted` is not, the recovery succeeded but the
 * UI is stuck on a stale failure surface — that's a real bug.
 */
function AnalysisComponentMounted({ ticker, hasData, recoveredFromCache, analysisStatus, attemptId, data, userEmail }) {
  // Phase 6.BH+ FIX — keep a ref to latest `data` so the chart-ready
  // listener (attached once on mount) always reads fresh values.
  const dataRef = React.useRef(data);
  React.useEffect(() => { dataRef.current = data; }, [data]);
  React.useEffect(() => {
    try {
      emitLifecycle("analysis_component_mounted", {
        ticker,
        recovered_from_cache: !!recoveredFromCache,
        analysis_status: analysisStatus || null,
        attempt_id: attemptId,
        extra: {
          had_data_on_mount: !!hasData,
        },
      });
      // Phase 6.AY — Render-confirmation event. Separate from the
      // legacy `analysis_component_mounted` ping so the Owner Console
      // "Last Rendered Analysis" card can read ONE row with the full
      // provenance that the user actually saw on screen.
      //
      // We send this AFTER the DOM commit (the parent has just
      // mounted) which proves the user is actually seeing the
      // rendered analysis — not just that the backend returned 200.
      const meta = (data && data._meta_debug) || {};
      const prov = meta.provenance || {};
      const canonical = (data && data.canonical_valuation) || {};
      const cacheLayers = prov.cache_layers_used || meta.cache_layers_used || data?.cache_layers_used || null;
      const skippedStages = data?.skipped_stages || meta.skipped_stages || null;
      // Phase 6.BH+ — pull explicit AI diagnostic fields the backend
      // now stamps at top-level, with safe fallbacks to the
      // `_meta_debug.ai_diagnostics` block for older responses.
      const aiDiag = (meta && meta.ai_diagnostics) || {};
      emitLifecycle("analysis_rendered", {
        ticker: (data && data.ticker) || ticker,
        company_name: data?.company_name || data?.profile?.company_name || null,
        request_id: meta.request_id || prov.request_id || data?.request_id || null,
        analysis_id: data?.analysis_id || prov.analysis_id || meta.analysis_id || null,
        data_source: prov.data_source || meta.data_source || data?.data_source || null,
        cache_hit: typeof prov.cache_hit === "boolean" ? prov.cache_hit : (data?.cache_hit ?? null),
        cache_layers_used: cacheLayers,
        mock_used: typeof prov.mock_used === "boolean" ? prov.mock_used : null,
        fallback_used: typeof prov.fallback_used === "boolean" ? prov.fallback_used : null,
        endpoint_coverage_pct: prov.endpoint_coverage_pct ?? meta.endpoint_coverage_pct ?? null,
        chart_data_status: prov.chart_data_status || meta.chart_data_status || null,
        chart_points_count: prov.chart_points_count ?? null,
        // Phase 6.BE — additional render-confirmation provenance
        // fields so Owner Console can show the EXACT visible page
        // state (no more "unknown" cells).
        result_validity: data?.result_validity || meta.result_validity || null,
        ai_summary_status: data?.ai_summary_status || data?.ai?.status || null,
        llm_skipped_reason: data?.llm_skipped_reason || null,
        last_completed_stage: data?.last_completed_stage || meta.last_completed_stage || null,
        skipped_stages: skippedStages,
        partial_response: data?.partial_response ?? null,
        canonical_current_price: canonical.current_price ?? null,
        canonical_fair_value: canonical.fair_value ?? null,
        canonical_upside_downside_pct: canonical.upside_pct ?? null,
        canonical_valuation_status: canonical.valuation_status || null,
        canonical_score: data?.opportunity_score ?? data?.score ?? null,
        // Phase 6.BH+ — explicit AI diagnostic fields. Backend now
        // stamps these at top-level on the analyze response, with
        // graceful fall-through to `_meta_debug.ai_diagnostics` for
        // backward compatibility with older bundles.
        ai_model_primary: data?.ai_model_primary ?? aiDiag.ai_primary_model ?? aiDiag.ai_model_requested ?? null,
        ai_model_fallback: data?.ai_model_fallback ?? aiDiag.ai_fallback_model ?? aiDiag.ai_retry_model ?? null,
        ai_model_used: data?.ai_model_used ?? aiDiag.ai_model_used ?? null,
        ai_fallback_attempted: (
          typeof data?.ai_fallback_attempted === "boolean" ? data.ai_fallback_attempted
            : (typeof aiDiag.ai_fallback_attempted === "boolean" ? aiDiag.ai_fallback_attempted : null)
        ),
        ai_fallback_success: (
          typeof data?.ai_fallback_success === "boolean" ? data.ai_fallback_success
            : (typeof aiDiag.ai_fallback_success === "boolean" ? aiDiag.ai_fallback_success : null)
        ),
        ai_error_type: data?.ai_error_type ?? aiDiag.ai_error_type ?? null,
        ai_skipped_reason: data?.ai_skipped_reason ?? data?.llm_skipped_reason ?? aiDiag.ai_skip_reason ?? null,
        ai_duration_ms: data?.ai_duration_ms ?? aiDiag.ai_duration_ms ?? null,
        // Phase 6.BI — per-model durations + outer budget telemetry.
        ai_primary_duration_ms: data?.ai_primary_duration_ms ?? aiDiag.ai_primary_duration_ms ?? null,
        ai_fallback_duration_ms: data?.ai_fallback_duration_ms ?? aiDiag.ai_fallback_duration_ms ?? null,
        ai_budget_total_ms: data?.ai_budget_total_ms ?? aiDiag.ai_budget_total_ms ?? null,
        ai_budget_remaining_before_start_ms: data?.ai_budget_remaining_before_start_ms ?? aiDiag.ai_budget_remaining_before_start_ms ?? null,
        ai_tokens_in: data?.ai_tokens_in ?? aiDiag.ai_tokens_in ?? null,
        ai_tokens_out: data?.ai_tokens_out ?? aiDiag.ai_tokens_out ?? null,
        ai_cost_usd: data?.ai_cost_usd ?? aiDiag.ai_cost_usd ?? null,
        llm_cache_hit: (
          typeof data?.llm_cache_hit === "boolean" ? data.llm_cache_hit
            : (typeof aiDiag.llm_cache_hit === "boolean" ? aiDiag.llm_cache_hit : null)
        ),
        ai_mode: data?.ai_mode ?? aiDiag.ai_mode ?? null,
        // Phase 6.BI-7 — new diagnostic fields.
        ai_cancel_source: data?.ai_cancel_source ?? aiDiag.ai_cancel_source ?? "none",
        ai_status_final: data?.ai_status_final ?? data?.ai_summary_status ?? null,
        ai_model_requested: data?.ai_model_requested ?? aiDiag.ai_model_requested ?? null,
        ai_primary_attempted: (
          typeof data?.ai_primary_attempted === "boolean" ? data.ai_primary_attempted : null
        ),
        ai_primary_success: (
          typeof data?.ai_primary_success === "boolean" ? data.ai_primary_success : null
        ),
        ai_primary_error_type: data?.ai_primary_error_type ?? null,
        ai_fallback_error_type: data?.ai_fallback_error_type ?? null,
        ai_budget_remaining_before_primary_ms: data?.ai_budget_remaining_before_primary_ms ?? null,
        ai_budget_remaining_before_fallback_ms: data?.ai_budget_remaining_before_fallback_ms ?? null,
        llm_cache_status: data?.llm_cache_status ?? null,
        llm_cache_write: (typeof data?.llm_cache_write === "boolean" ? data.llm_cache_write : null),
        ai_primary_requested: data?.ai_primary_requested ?? aiDiag.ai_primary_requested ?? null,
        ai_routing_reason: data?.ai_routing_reason ?? aiDiag.routing_reason ?? null,
        // Phase 6.BI-11 — plan-aware AI fields.
        user_plan: data?.user_plan ?? null,
        ai_budget_strategy: data?.ai_budget_strategy ?? aiDiag.ai_mode ?? null,
        full_ai_generated: (typeof data?.full_ai_generated === "boolean" ? data.full_ai_generated : null),
        locked_sections_generated: (typeof data?.locked_sections_generated === "boolean" ? data.locked_sections_generated : null),
        route: (typeof window !== "undefined") ? window.location.pathname : null,
        frontend_route: (typeof window !== "undefined") ? window.location.pathname : null,
        rendered_at: new Date().toISOString(),
        user_email: userEmail || null,
        extra: {
          analysis_status: analysisStatus || null,
          recovered_from_cache: !!recoveredFromCache,
          live_data_only_mode: prov.live_data_only_mode ?? null,
          live_data_only_violation: prov.live_data_only_violation ?? null,
        },
      });
    } catch { /* swallow */ }
    // Phase 6.BH+ FIX — Listen for chart-ready event from PriceChart
    // and re-emit a follow-up `analysis_rendered` with the actual
    // chart_data_status + chart_points_count. The lifecycle handler
    // upserts on `_id="latest"` so the second event overwrites the
    // chart fields on `last_rendered_analysis` without disturbing the
    // canonical_* / ai_* fields (the handler `_pick`s frontend > audit).
    //
    // We use a window event because PriceChart is rendered deep in the
    // tree and doesn't have a direct prop bridge to this component.
    const handler = (ev) => {
      try {
        const det = (ev && ev.detail) || {};
        if (!det.ticker || det.ticker !== ticker) return;
        const d = dataRef.current;
        const meta = (d && d._meta_debug) || {};
        const prov = meta.provenance || {};
        const canonical = (d && d.canonical_valuation) || {};
        const aiDiag = (meta && meta.ai_diagnostics) || {};
        emitLifecycle("analysis_rendered", {
          ticker,
          // Re-stamp the canonical fields so the second event keeps
          // them populated (the lifecycle handler always REPLACES the
          // doc, it doesn't merge).
          request_id: meta.request_id || prov.request_id || d?.request_id || null,
          analysis_id: d?.analysis_id || prov.analysis_id || meta.analysis_id || null,
          data_source: prov.data_source || meta.data_source || d?.data_source || null,
          cache_hit: typeof prov.cache_hit === "boolean" ? prov.cache_hit : (d?.cache_hit ?? null),
          cache_layers_used: prov.cache_layers_used || meta.cache_layers_used || d?.cache_layers_used || null,
          result_validity: d?.result_validity || meta.result_validity || null,
          ai_summary_status: d?.ai_summary_status || d?.ai?.status || null,
          partial_response: d?.partial_response ?? null,
          canonical_current_price: canonical.current_price ?? null,
          canonical_fair_value: canonical.fair_value ?? null,
          canonical_upside_downside_pct: canonical.upside_pct ?? null,
          canonical_valuation_status: canonical.valuation_status || null,
          canonical_score: d?.opportunity_score ?? d?.score ?? null,
          ai_model_primary: d?.ai_model_primary ?? aiDiag.ai_primary_model ?? null,
          ai_model_fallback: d?.ai_model_fallback ?? aiDiag.ai_fallback_model ?? null,
          ai_model_used: d?.ai_model_used ?? aiDiag.ai_model_used ?? null,
          ai_mode: d?.ai_mode ?? aiDiag.ai_mode ?? null,
          ai_cancel_source: d?.ai_cancel_source ?? aiDiag.ai_cancel_source ?? "none",
          ai_status_final: d?.ai_status_final ?? d?.ai_summary_status ?? null,
          ai_model_requested: d?.ai_model_requested ?? aiDiag.ai_model_requested ?? null,
          ai_primary_attempted: (typeof d?.ai_primary_attempted === "boolean" ? d.ai_primary_attempted : null),
          ai_primary_success: (typeof d?.ai_primary_success === "boolean" ? d.ai_primary_success : null),
          ai_primary_error_type: d?.ai_primary_error_type ?? null,
          ai_fallback_error_type: d?.ai_fallback_error_type ?? null,
          llm_cache_status: d?.llm_cache_status ?? null,
          llm_cache_write: (typeof d?.llm_cache_write === "boolean" ? d.llm_cache_write : null),
          ai_primary_requested: d?.ai_primary_requested ?? aiDiag.ai_primary_requested ?? null,
          ai_routing_reason: d?.ai_routing_reason ?? aiDiag.routing_reason ?? null,
          user_plan: d?.user_plan ?? null,
          ai_budget_strategy: d?.ai_budget_strategy ?? aiDiag.ai_mode ?? null,
          full_ai_generated: (typeof d?.full_ai_generated === "boolean" ? d.full_ai_generated : null),
          locked_sections_generated: (typeof d?.locked_sections_generated === "boolean" ? d.locked_sections_generated : null),
          ai_skipped_reason: d?.ai_skipped_reason ?? d?.llm_skipped_reason ?? null,
          llm_cache_hit: (typeof d?.llm_cache_hit === "boolean" ? d.llm_cache_hit : null),
          // The new chart fields from the actual chart load.
          chart_data_status: det.chart_data_status || null,
          chart_points_count: (typeof det.points_count === "number" ? det.points_count : null),
          route: (typeof window !== "undefined") ? window.location.pathname : null,
          frontend_route: (typeof window !== "undefined") ? window.location.pathname : null,
          rendered_at: new Date().toISOString(),
          user_email: userEmail || null,
          extra: {
            analysis_status: analysisStatus || null,
            chart_update: true,
          },
        });
      } catch { /* swallow */ }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("stockaily:chart_ready", handler);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("stockaily:chart_ready", handler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}


export default function Analyze() {
  const { ticker } = useParams();
  const tk = (ticker || "").toUpperCase();
  const navigate = useNavigate();
  // Phase 6.BI-13 — admin QA Plan Simulation. When the URL carries
  // `?simulate_plan=free|pro|premium`, the analyze POST forwards it as
  // a query param so the backend treats the request as if the user
  // were on that plan. The backend STRICTLY ignores `simulate_plan`
  // for non-admin callers, so this is safe to send on every request.
  const [searchParams] = useSearchParams();
  const simulatePlanRaw = (searchParams.get("simulate_plan") || "").toLowerCase();
  const simulatePlan = ["free", "pro", "premium"].includes(simulatePlanRaw)
    ? simulatePlanRaw
    : null;
  const { user, loading: authLoading, setUser, authProbedUnauthed } = useAuth();
  const { t } = useTranslation();
  const [snap, setSnap] = useState(null);
  const [, setLoadingSnap] = useState(true);
  const [data, setData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  // Stage of the in-flight analyze call. `null` when idle. Drives the
  // staged loading copy ("Fetching market data" → "Running valuation"
  // → "Generating AI insights" → "Finalizing").
  const [analyzeStage, setAnalyzeStage] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [limited, setLimited] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [notFoundReason, setNotFoundReason] = useState(null);
  // Phase 6.AB: when an analyze fails server-side and the caller is an
  // admin, the backend includes traceback + error class in the response
  // detail. We stash it here so the inline admin error panel can render
  // it directly on the analyze page (no need to dig through devtools).
  const [adminError, setAdminError] = useState(null);
  // Phase 6.AE — last seen request_id for the failed analyze attempt.
  // Shown to admins on the "Something went wrong" / "Stock not found"
  // surfaces so the operator can paste it into Owner Console / Mongo
  // to find the exact audit row without leaving the failure page.
  const [lastRequestId, setLastRequestId] = useState(null);

  // Phase 6.AK — Refs to track "did the user-action actually fire?"
  // signals that survive even when React state updates haven't yet
  // committed. The TimeoutScreenTelemetry component reads these to
  // include `did_fetch_start` / `did_backend_request_start` in the
  // outgoing event, so the operator can tell at a glance whether the
  // timeout screen is genuine (fetch fired, backend slow) or stale
  // (no fetch ever started, user is staring at React state from a
  // previous render).
  const didFetchStartRef = React.useRef(false);
  const didBackendRequestStartRef = React.useRef(false);

  // Phase 6.AU — RESULT-WINS state machine refs.
  //
  // The user reported the canonical bug: backend returns 200 OK + recovery
  // polling hits + lifecycle emits render_completed, but the visible UI
  // is still stuck on "Analysis took too long". This is a state-priority
  // problem — a stale `notFoundReason` from an earlier attempt (or from
  // a parallel runAnalyze invocation) was lingering AFTER a valid result
  // arrived. These refs and the useEffect below enforce the hard rule:
  // **a valid result for the current ticker ALWAYS wins over any
  // timeout/error state, regardless of when each was set**.
  //
  // `currentAttemptIdRef` — monotonically increments at the start of
  //   every runAnalyze invocation. Lets each setReason/setData call tag
  //   itself so we can detect stale updates.
  // `latestSuccessAttemptIdRef` — id of the most recent attempt that
  //   actually produced a valid result. If a setReason call later fires
  //   with a smaller attempt id, it's stale and is ignored.
  // `timeoutScreenShownAtRef` — wall-clock when the timeout/error screen
  //   was last rendered. Used by the new lifecycle events so the operator
  //   can prove from telemetry alone that the screen was cleared.
  const currentAttemptIdRef = React.useRef(0);
  const latestSuccessAttemptIdRef = React.useRef(0);
  const timeoutScreenShownAtRef = React.useRef(null);
  // Per-ticker client trace id — generated in the route_entry effect
  // and threaded through every subsequent lifecycle event for THIS
  // analyze attempt. Lets the Owner Console group route_entry →
  // fetch_started → response_received → render_completed even when
  // no backend request_id has been issued yet (e.g. the orphan-
  // timeout case where the request never reached the backend).
  const clientTraceIdRef = React.useRef(null);
  // Previous tk seen by the route_entry effect. Used to detect "user
  // navigated from a stale failure screen to a new ticker" so we can
  // log the previous ticker in route_entry telemetry.
  const previousTickerRef = React.useRef(null);
  // What the user actually typed in the search input that brought
  // them here. Populated by reading `stockaily.ticker_provenance`
  // from sessionStorage at route_entry time.
  const typedTickerRef = React.useRef(null);

  // Phase 6.Y: No frontend-imposed analyze timeout.
  // Production must never artificially abort a valid analysis request.
  // The browser still has its own intrinsic network limits, but we do
  // not add any of our own. Backend stalls surface as explicit HTTP
  // errors (500/structured) which we handle in the catch block.

  // Phase 6.AK — ROUTE-LEVEL INSTRUMENTATION.
  // ----------------------------------------------------------------
  // This useEffect runs UNCONDITIONALLY on every /analyze/:ticker
  // mount and on every `tk` change. It runs BEFORE any auth check,
  // BEFORE any data fetch. Its only job is to:
  //   1. Generate a fresh client_trace_id for this attempt
  //   2. HARD-RESET all state that could be holding stale ticker
  //      data from a previous render (the "AAPL timeout still showing
  //      while user navigates to MU" bug class)
  //   3. Emit a `route_entry` lifecycle event so the Owner Console
  //      can prove the Analyze page mounted for this ticker — even
  //      if everything else (auth gate, network, fetch) fails
  //      afterward.
  //
  // The auth-gated effect below this one will then either start a
  // real analyze (and emit `fetch_started`) or emit
  // `route_entry_blocked` with the exact reason it didn't fire.
  useEffect(() => {
    // 1) Mint a new trace id for this attempt.
    const traceId = newClientTraceId();
    clientTraceIdRef.current = traceId;
    // 2) Reset request-state refs.
    didFetchStartRef.current = false;
    didBackendRequestStartRef.current = false;
    // 3) HARD STATE RESET (regardless of auth). This is the critical
    //    fix for "AAPL timeout still showing when user navigates to
    //    MU". The previous useEffect only cleared `notFoundReason`
    //    if the user was authed; if auth had quietly expired the
    //    stale state would leak across navigations.
    const prev = previousTickerRef.current;
    previousTickerRef.current = tk;
    setData(null);
    setNotFoundReason(null);
    setAdminError(null);
    setLastRequestId(null);
    setLimited(false);
    // Re-read provenance so we can include `typed_ticker` on the
    // route_entry event itself (without waiting for runAnalyze).
    const _prov = readTickerProvenance();
    typedTickerRef.current = _prov?.typed_uppercase || null;
    // 4) Emit route_entry — unconditional, before auth check.
    emitLifecycle("route_entry", {
      ticker: tk,
      client_trace_id: traceId,
      user_email: user?.email || null,
      extra: {
        route_param_ticker: ticker,
        normalized_ticker: tk,
        url_path_ticker: (typeof window !== "undefined")
          ? (window.location.pathname.match(/\/analyze\/([A-Za-z0-9.\-]+)/) || [])[1] || null
          : null,
        previous_ticker: prev || null,
        typed_ticker: typedTickerRef.current,
        typed_source: _prov?.source || null,
        typed_age_ms: _prov ? (Date.now() - (_prov.ts || 0)) : null,
        auth_loading: !!authLoading,
        has_user: !!user,
        user_plan: user?.plan || null,
        backend_url: RESOLVED_API_BASE || null,
      },
    });
    // Phase 6.AZ — Poll the public /_health/identity endpoint to read
    // the latest cache-bust token. If the operator clicked "Clear all
    // caches" since our last visit, this triggers a sessionStorage
    // flush BEFORE we issue the analyze request — so no stale
    // ticker_provenance / lifecycle trace can contaminate the new run.
    // Fire-and-forget; never blocks the analyze UX.
    pollAndApplyCacheBust(api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tk]);

  // Phase 6.AU — RESULT-WINS state enforcer.
  //
  // This useEffect runs on every change of `data`. When a valid analysis
  // payload arrives (from a direct POST 200, from recovery polling, or
  // from cache hit), it FORCE-CLEARS every error/loading/timeout state
  // that could overlap with rendering the analysis. This is the hard
  // rule the user asked for: "If recovery_poll_hit happens and the
  // result is valid, the timeout screen must disappear."
  //
  // Why a separate effect instead of inline state-clears in the success
  // path? Because the bug was a state-priority RACE — a stale
  // notFoundReason from an earlier `setReason("timeout")` was lingering
  // AFTER the success path. A single useEffect keyed on `data` is the
  // last word: whenever `data` is valid, any other error state
  // is unconditionally wiped. This survives any future code path that
  // forgets to clear one of these flags.
  useEffect(() => {
    if (!data || !data.ticker) return;
    // Promote this attempt to "latest success". Any subsequent
    // setReason call tagged with a smaller attempt id will be ignored
    // by the render-time guard.
    latestSuccessAttemptIdRef.current = currentAttemptIdRef.current;
    let cleared = false;
    if (notFoundReason !== null) {
      emitLifecycle("stale_timeout_ignored", {
        ticker: data.ticker,
        prior_reason: notFoundReason,
        prior_request_id: lastRequestId || null,
        winning_attempt_id: currentAttemptIdRef.current,
        timeout_screen_shown_at: timeoutScreenShownAtRef.current,
        extra: {
          recovered_from_cache: !!data.recovered_from_cache,
          analysis_status: data.analysis_status || null,
          data_source: data.data_source || null,
        },
      });
      setNotFoundReason(null);
      cleared = true;
    }
    if (adminError) {
      setAdminError(null);
      cleared = true;
    }
    if (analyzing) {
      setAnalyzing(false);
      cleared = true;
    }
    if (analyzeStage !== null) {
      setAnalyzeStage(null);
      cleared = true;
    }
    emitLifecycle("result_state_set", {
      ticker: data.ticker,
      attempt_id: currentAttemptIdRef.current,
      cleared_stale_state: cleared,
      recovered_from_cache: !!data.recovered_from_cache,
      analysis_status: data.analysis_status || null,
      extra: {
        cache_hit: !!data.cache_hit,
        data_source: data.data_source || null,
        prior_request_id: lastRequestId || null,
      },
    });
    if (cleared && timeoutScreenShownAtRef.current) {
      const shown_ms = Date.now() - timeoutScreenShownAtRef.current;
      emitLifecycle("timeout_screen_cleared", {
        ticker: data.ticker,
        attempt_id: currentAttemptIdRef.current,
        shown_for_ms: shown_ms,
        extra: { reason_for_clear: "data_set" },
      });
      timeoutScreenShownAtRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Phase 6.BI-24 — AI STATUS POLLING.
  // ----------------------------------------------------------------
  // While the response carries `ai_status_final ∈ {ai_generating,
  // cache_hit_needs_ai_repair}`, poll /api/analyze/{ticker}/ai-status
  // every 3 seconds for up to 90 seconds. When the backend's
  // background job completes (or the BI-24 cache repair runs to
  // completion), it overwrites the cached row with the final
  // narrative + a terminal `ai_status_final` (`full_llm_success` /
  // `metric_narrative_success` / `ai_failed`). We splice that into
  // the existing `data` state so the AI Insights panel auto-upgrades
  // in place — no reload required.
  //
  // After the 90s window expires without a terminal status, we stop
  // polling and surface a calm message via the
  // `ai_generating_timed_out` flag (the AI panel reads it and shows
  // "AI insights are still being generated. They will appear
  // automatically when ready.").
  useEffect(() => {
    const status = data?.ai_status_final || data?.ai_summary_status;
    const isGenerating =
      status === "ai_generating" || status === "cache_hit_needs_ai_repair";
    if (!isGenerating || !data?.ticker) return;

    const ticker = data.ticker;
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 3000;
    const POLL_WINDOW_MS = 90_000;
    let cancelled = false;
    let timer = null;

    const sim = simulatePlan ? { params: { simulate_plan: simulatePlan } } : {};

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`/analyze/${ticker}/ai-status`, {
          ...sim,
          timeout: 10_000,
        });
        const body = res?.data || {};
        const finalStatus = (body.ai_status_final || "").toLowerCase();
        const isTerminal =
          finalStatus === "full_llm_success"
          || finalStatus === "fallback_llm_success"
          || finalStatus === "metric_narrative_success"
          || finalStatus === "cache_hit_full_ai"
          || finalStatus === "ai_failed";
        if (isTerminal && !cancelled) {
          // Merge the new AI block + narrative into existing data.
          setData((prev) => {
            if (!prev || prev.ticker !== ticker) return prev;
            const next = { ...prev };
            if (body.ai && typeof body.ai === "object") {
              next.ai = { ...(prev.ai || {}), ...body.ai };
            }
            next.ai_status_final = body.ai_status_final;
            next.ai_summary_status =
              body.ai_summary_status || body.ai_status_final;
            if (body.ai_model_used) next.ai_model_used = body.ai_model_used;
            if (typeof body.structured_ai_sanitized === "boolean") {
              next.structured_ai_sanitized = body.structured_ai_sanitized;
            }
            if (body.narrative_status) {
              next.narrative_status = body.narrative_status;
            }
            // Splice the narrative into valuation.institutional.narrative
            if (body.narrative && typeof body.narrative === "object") {
              const val = { ...(prev.valuation || {}) };
              const inst = { ...(val.institutional || {}) };
              inst.narrative = body.narrative;
              val.institutional = inst;
              next.valuation = val;
            }
            // Clear the "timed out" hint just in case it had been set.
            next.ai_generating_timed_out = false;
            // Mark partial_response false so any banners stand down.
            next.partial_response = false;
            return next;
          });
          try {
            emitLifecycle("ai_status_poll_terminal", {
              ticker,
              status: finalStatus,
              elapsed_ms: Date.now() - startedAt,
            });
          } catch { /* swallow */ }
          return;
        }
      } catch (err) {
        // Auth failures stop polling immediately; transient errors keep retrying.
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          try {
            emitLifecycle("ai_status_poll_auth_failed", {
              ticker,
              status_code: code,
            });
          } catch { /* swallow */ }
          return;
        }
      }
      if (cancelled) return;
      if (Date.now() - startedAt > POLL_WINDOW_MS) {
        // Stop aggressive polling and let the AI panel calm the user.
        setData((prev) => {
          if (!prev || prev.ticker !== ticker) return prev;
          if (prev.ai_generating_timed_out) return prev;
          return { ...prev, ai_generating_timed_out: true };
        });
        try {
          emitLifecycle("ai_status_poll_window_exceeded", {
            ticker,
            window_ms: POLL_WINDOW_MS,
          });
        } catch { /* swallow */ }
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, POLL_INTERVAL_MS);
    try {
      emitLifecycle("ai_status_poll_started", {
        ticker,
        initial_status: status,
        interval_ms: POLL_INTERVAL_MS,
        window_ms: POLL_WINDOW_MS,
      });
    } catch { /* swallow */ }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ai_status_final, data?.ticker]);



  // Snapshot (works without auth)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingSnap(true);
        const res = await api.get(`/stocks/${tk}`);
        if (!cancelled) setSnap(res.data.metrics);
      } catch {
        if (!cancelled) setSnap(null);
      } finally { if (!cancelled) setLoadingSnap(false); }
    })();
    return () => { cancelled = true; };
  }, [tk]);

  // Phase 6.AO — bounded auth wait + fallback.
  // ----------------------------------------------------------------
  // The production failure mode reported by the user:
  //   * /auth/me hangs (cold start, slow replica, network blip)
  //   * AuthContext keeps `loading=true` forever
  //   * This useEffect bailed on `if (authLoading) return`
  //   * runAnalyze never fired, the user saw a stale timeout from
  //     a previous AAPL attempt
  //
  // The fix has two parts:
  //   (a) AuthContext now hard-caps /auth/me at 4s (Phase 6.AO).
  //   (b) This effect refuses to wait indefinitely. After
  //       AUTH_WAIT_BUDGET_MS, if a localStorage token exists we
  //       proceed with the analyze (the backend validates it; if the
  //       token is dead, the analyze returns 401 and we render the
  //       auth gate). If there's no token, we proceed only when free
  //       analysis is allowed — for now, we fall back to the auth
  //       gate path (existing behavior) instead of starting the
  //       analyze, so we never silently 401 in the loading-spinner.
  // Phase 6.AP — REDUCED auth wait budget per user feedback.
  // User explicitly requested 2-3 seconds, not 4.5s. Production
  // /auth/me hangs longer than the previous budget were a smoking
  // gun.
  const AUTH_WAIT_BUDGET_MS = 2500;
  const authWaitTimerRef = React.useRef(null);
  const proceededRef = React.useRef(false);

  useEffect(() => {
    // Cleanup any pending wait timer from a previous tk.
    return () => {
      if (authWaitTimerRef.current) {
        clearTimeout(authWaitTimerRef.current);
        authWaitTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Reset the proceed flag for this tk (the parent wrapper uses
    // key={ticker} so this component is fresh anyway, but defensive).
    proceededRef.current = false;
    const proceed = (reasonForProceed) => {
      if (proceededRef.current) return;
      proceededRef.current = true;
      try {
        emitLifecycle("auth_gate_resolved", {
          ticker: tk,
          client_trace_id: clientTraceIdRef.current,
          extra: {
            outcome: reasonForProceed,
            had_token: hasAuthHint(),
            had_user: !!user,
            elapsed_in_loading_ms:
              authWaitTimerRef.current ? AUTH_WAIT_BUDGET_MS : 0,
          },
        });
      } catch { /* swallow */ }
      runAnalyze();
      refreshWatchlistStatus();
    };

    if (!authLoading) {
      // Auth has resolved — proceed or block on real signals.
      if (user) {
        proceed("auth_resolved_user");
      } else {
        // Phase 6.AQ — CRITICAL FIX. The user reported "I'm logged in
        // in the navbar but Analyze sees no session". Root cause: /auth/me
        // timed out, AuthContext set user=null but KEPT the token.
        // Previously we then emitted route_entry_blocked and waited for
        // the user to "sign in" (which they were already!), never firing
        // runAnalyze.
        //
        // New rule: if we have a token, NEVER block on "no user". The
        // token is what the backend actually validates. Let runAnalyze
        // attach Authorization and find out from the backend whether
        // the session is genuinely dead (401) or fine (200). The
        // backend is the only authority on token validity.
        //
        // Phase 6.AV — EXTENDED FIX. The previous version still blocked
        // when there was no JS-visible token at all. But production
        // users authed via Google OAuth (or any flow that landed only
        // a `session_token` httpOnly cookie) have NO JS-readable
        // credential — cookies marked `httponly` are unreadable from
        // JavaScript by design. The route_entry_blocked +
        // no_user_session_no_token telemetry the user reported was
        // exactly this case: cookie session was valid, but the gate
        // refused to fire because it couldn't see the cookie.
        //
        // The backend's `authenticate_request` already accepts the
        // cookie as the highest-priority credential. So if we proceed
        // optimistically WITHOUT a JS-visible signal, the backend
        // either:
        //   * validates the cookie -> 200 OK (analyze runs)
        //   * sees no credential -> 401 -> our global axios 401 handler
        //     dispatches `stockaily:auth_failed`, AuthContext drops user,
        //     and the existing auth-gate UI surfaces correctly.
        // No silent block. The backend is the authority.
        const hasToken = (typeof window !== "undefined")
          && hasAuthHint();
      // Analyze uses hasAuthHint() for UX gating only; the backend
      // remains the authoritative auth boundary via the HttpOnly
      // session_token cookie.
        if (hasToken) {
          proceed("auth_resolved_no_user_but_token");
        } else {
          try {
            emitLifecycle("route_entry_optimistic_proceed", {
              ticker: tk,
              client_trace_id: clientTraceIdRef.current,
              extra: {
                reason: "no_js_visible_auth_signal_letting_backend_decide",
                has_user: false,
                has_token: false,
                note: (
                  "Cookie session may still be valid (httponly, unreadable "
                  + "from JS). Backend will adjudicate via /api/analyze."
                ),
              },
            });
          } catch { /* swallow */ }
          proceed("auth_resolved_optimistic_cookie_check");
        }
      }
      return;
    }

    // authLoading is true — wait, but only up to AUTH_WAIT_BUDGET_MS.
    // Phase 6.AQ — Also: if we have a token already, we don't actually
    // need to wait for /auth/me. The token alone is sufficient for the
    // analyze fetch. Skip the wait entirely and proceed.
    const hasTokenNow = (typeof window !== "undefined")
      && hasAuthHint();
      // WS-8: cookie-only auth — UI hint proxies "did we auth here?"
    if (hasTokenNow) {
      proceed("auth_skipped_token_present");
      return;
    }
    emitLifecycle("route_entry_blocked", {
      ticker: tk,
      client_trace_id: clientTraceIdRef.current,
      extra: { reason: "auth_loading", will_proceed_after_ms: AUTH_WAIT_BUDGET_MS },
    });
    authWaitTimerRef.current = setTimeout(() => {
      // Re-check at fire-time (`user` / `authLoading` may have
      // updated meanwhile; if so we'd already be in the !authLoading
      // branch above and proceededRef would be true).
      if (proceededRef.current) return;
      const hasToken = hasAuthHint();
      if (hasToken) {
        // We have a token but auth resolution hung. Start the
        // analyze anyway — the backend will validate the token. If
        // it's dead we'll get a 401 and surface a clear auth gate.
        // If it's good, the analyze proceeds normally.
        emitLifecycle("auth_wait_budget_exceeded", {
          ticker: tk,
          client_trace_id: clientTraceIdRef.current,
          extra: {
            budget_ms: AUTH_WAIT_BUDGET_MS,
            action: "proceed_with_stored_token",
            note: "auth_me hung; relying on Authorization header validity",
          },
        });
        proceed("auth_bypass_token_fallback");
      } else {
        // No token AND auth still loading.
        //
        // Phase 6.AV — OPTIMISTIC PROCEED.
        // Previously we stopped here on the assumption "no token + no
        // user = unauthenticated". But production users authed via
        // Google OAuth (or any cookie-only flow) have NO JS-visible
        // credential — the `session_token` cookie is `httponly` and
        // therefore unreadable from JS. Stopping here silently
        // mis-classified them as unauthed and the user never saw
        // anything happen.
        //
        // The backend is the only authority on session validity. Fire
        // the analyze with `withCredentials: true`. The browser
        // automatically attaches the cookie; the backend's
        // `authenticate_request` accepts cookies as the
        // highest-priority credential. If the cookie is also dead, the
        // 401 path runs (global axios interceptor → setUser(null) →
        // auth gate). Either way: no silent block.
        emitLifecycle("auth_wait_budget_exceeded", {
          ticker: tk,
          client_trace_id: clientTraceIdRef.current,
          extra: {
            budget_ms: AUTH_WAIT_BUDGET_MS,
            action: "proceed_optimistic_cookie_check",
            note: (
              "no JS-visible auth signal; backend may still validate "
              + "via session_token cookie (httponly)"
            ),
          },
        });
        try {
          emitLifecycle("route_entry_optimistic_proceed", {
            ticker: tk,
            client_trace_id: clientTraceIdRef.current,
            extra: {
              reason: "auth_wait_budget_exceeded_no_token",
              has_user: false,
              has_token: false,
            },
          });
        } catch { /* swallow */ }
        proceed("auth_resolved_optimistic_cookie_check");
      }
    }, AUTH_WAIT_BUDGET_MS);

    return () => {
      if (authWaitTimerRef.current) {
        clearTimeout(authWaitTimerRef.current);
        authWaitTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tk, authLoading, user?.user_id]);

  const runAnalyze = async (opts = {}) => {
    // Phase 6.AO/AV — fire the analyze REGARDLESS of JS-visible auth
    // signals. The previous version returned early when `!user &&
    // !hasToken`, which silently mis-classified cookie-only authed
    // users (Google OAuth) as logged out and never fired the request.
    //
    // The backend is the authority on auth validity. It accepts:
    //   1. `session_token` cookie (highest priority — unreadable from JS)
    //   2. Authorization Bearer JWT (from localStorage)
    //   3. Authorization Bearer session_token
    // The axios client attaches both via `withCredentials: true` AND
    // the request interceptor. If ALL credentials are missing/invalid,
    // the backend returns 401 and our global response interceptor
    // dispatches `stockaily:auth_failed` -> AuthContext drops user ->
    // auth gate renders. No silent block.
    const isRetry = !!opts.isRetry;
    // Phase 6.AU — bump the attempt id at the very top of every
    // runAnalyze invocation. Every setReason / setData below tags
    // itself with this id (via the closure) so the render-time guard
    // and the result-wins useEffect can detect stale updates from a
    // PRIOR invocation that resolved late.
    currentAttemptIdRef.current += 1;
    const attemptId = currentAttemptIdRef.current;
    // Phase 6.AI.2 — local trackers so the `finally` block can emit a
    // frontend_errors row with the SAME context the catch block decided
    // on, without relying on React state being updated synchronously.
    // We use these scoped helpers everywhere we'd normally call the
    // state setter so the local mirror stays in sync.
    let localNotFoundReason = null;
    let localRequestId = null;
    const setReason = (r, lifecycleExtra = {}) => {
      // Phase 6.AU — STALE-REASON GUARD.
      //
      // If a later attempt has already produced a successful result,
      // this setReason call belongs to a now-irrelevant attempt and
      // must NOT clobber the visible state. Drop it on the floor and
      // emit a lifecycle event so the operator can see it happened.
      if (r && latestSuccessAttemptIdRef.current >= attemptId
          && latestSuccessAttemptIdRef.current > 0) {
        try {
          if (typeof _lifeCycleEmit === "function") {
            _lifeCycleEmit("stale_reason_dropped", {
              reason: r,
              stale_attempt_id: attemptId,
              winning_attempt_id: latestSuccessAttemptIdRef.current,
              ...lifecycleExtra,
            });
          }
        } catch { /* swallow */ }
        return;
      }
      localNotFoundReason = r;
      setNotFoundReason(r);
      // Phase 6.AI.4 — every setReason call EMITS a lifecycle event with
      // the canonical request_id (now stamped in localRequestId from
      // the X-Request-ID response header). This lets the Owner Console
      // show the EXACT state transition that produced each error UI.
      if (r) {
        try {
          // logLifecycle is defined below — call it via a closure-safe
          // reference set after definition.
          if (typeof _lifeCycleEmit === "function") {
            _lifeCycleEmit("error_state_set", {
              reason: r,
              attempt_id: attemptId,
              ...lifecycleExtra,
            });
          }
        } catch { /* swallow */ }
      }
    };
    const setReqId = (r) => { localRequestId = r; setLastRequestId(r); };
    // Forward-declared lifecycle emitter (assigned just after the
    // logLifecycle closure is defined below). Avoids a circular reference
    // and ensures setReason can emit even though logLifecycle is declared
    // later in this same function body.
    let _lifeCycleEmit = null;

    // Phase 6.AJ — Ticker provenance trace.
    // Capture every source of "ticker" so the Owner Console can prove
    // whether a mismatch happened anywhere along the chain. The user's
    // P0 bug report: they typed MU, Owner Console showed AAPL. Without
    // this provenance block we cannot definitively trace where the
    // divergence happened (or whether it happened at all).
    //
    // Sources, in order:
    //   1. typed_ticker          — what the user actually typed/clicked in
    //                              StockSearch (or any other CTA that
    //                              calls stashTickerProvenance). Read from
    //                              sessionStorage which is set just before
    //                              navigation. Stale after 60s.
    //   2. route_param_ticker    — react-router useParams() raw value
    //   3. normalized_ticker     — useParams() value after uppercase+trim
    //   4. url_path_ticker       — parsed from window.location.pathname
    //                              via regex (independent of useParams)
    //   5. final_api_url         — fully resolved axios URL (base + path)
    //   6. backend_returned_ticker (set later in response_received)
    //   7. runtime_ticker        — captured server-side and joined back
    //                              in the Owner Console
    const _provenance = readTickerProvenance();   // may be null if stale
    const urlPathTicker = (typeof window !== "undefined")
      ? (window.location.pathname.match(/\/analyze\/([A-Za-z0-9.\-]+)/) || [])[1] || null
      : null;
    const finalApiUrl = `${RESOLVED_API_BASE || "(empty base)"}/analyze/${tk}`;
    const baseProvenance = {
      typed_ticker: _provenance?.typed_uppercase || null,
      typed_raw: _provenance?.typed || null,
      typed_query: _provenance?.typed || null,             // Phase 6.BE — alias for clarity in Owner Console
      typed_source: _provenance?.source || null,
      typed_age_ms: _provenance ? (Date.now() - (_provenance.ts || 0)) : null,
      // Phase 6.BE — explicit suggestion-click provenance.
      // `selected_symbol` is the source of truth when the user clicked
      // a suggestion (e.g. typed "Micron" -> selected_symbol "MU").
      // When present, mismatch logic uses this instead of `typed_ticker`
      // so company-name searches stop generating false mismatches.
      selected_label: _provenance?.selected_label || null,
      selected_symbol: _provenance?.selected_symbol || null,
      canonical_ticker: (_provenance?.selected_symbol
                        || _provenance?.target
                        || tk
                        || (ticker || "").toUpperCase()
                        || null),
      route_param_ticker: (ticker || "").toUpperCase() || null,
      normalized_ticker: tk || null,
      url_path_ticker: (urlPathTicker || "").toUpperCase() || null,
      final_api_url: finalApiUrl,
      base_url: RESOLVED_API_BASE || null,
    };
    // Phase 6.BE — Mismatch logic redesigned:
    // Source-of-truth chain: canonical_ticker -> route_param -> normalized -> url_path -> backend_returned.
    // `typed_query` is informational ONLY when a suggestion was selected
    // (selected_symbol present). It is the source-of-truth only when the
    // user did a raw text submit without selecting a suggestion. This
    // prevents "typed=Micron" from being flagged against "MU" when the
    // selected suggestion already mapped Micron -> MU correctly.
    const provenanceMismatches = (() => {
      const out = [];
      const selSym  = baseProvenance.selected_symbol;
      const canon   = baseProvenance.canonical_ticker;
      const route   = baseProvenance.route_param_ticker;
      const norm    = baseProvenance.normalized_ticker;
      const urlP    = baseProvenance.url_path_ticker;
      const typedTk = baseProvenance.typed_ticker;

      // Canonical chain: must all agree.
      if (canon && route && canon !== route) out.push(`canonical(${canon})!=route(${route})`);
      if (route && norm  && route !== norm)  out.push(`route(${route})!=normalized(${norm})`);
      if (norm  && urlP  && norm  !== urlP)  out.push(`normalized(${norm})!=url_path(${urlP})`);

      // If a suggestion was explicitly selected, its `selected_symbol`
      // must agree with canonical. Otherwise it represents a genuine
      // suggestion-resolution bug.
      if (selSym && canon && selSym !== canon) {
        out.push(`selected_symbol(${selSym})!=canonical(${canon})`);
      }

      // Only fall back to typed-vs-route comparison when there was
      // NO suggestion click (`selected_symbol` is null). This is the
      // raw-text-submit path where the user typed "AAPL" directly.
      if (!selSym && typedTk && route && typedTk !== route) {
        // Filter out obvious company-name typings (look_like_company_name).
        const looksLikeCompanyName = baseProvenance.typed_raw
          && baseProvenance.typed_raw.length > 5
          && !/^[A-Z]{1,5}$/i.test(baseProvenance.typed_raw.trim());
        if (!looksLikeCompanyName) {
          out.push(`typed(${typedTk})!=route(${route})`);
        }
      }
      return out;
    })();
    baseProvenance.has_mismatch = provenanceMismatches.length > 0;
    baseProvenance.mismatches = provenanceMismatches;
    baseProvenance.consistency_status = provenanceMismatches.length === 0
      ? "CONSISTENT" : "MISMATCH";

    // Phase 6.AI.3 — Lifecycle telemetry. Every meaningful state
    // transition is reported to /api/_health/frontend_lifecycle so the
    // Owner Console can prove "backend returned 200, but the frontend
    // displayed timeout" exists and identify the exact stale-state path.
    // Fire-and-forget — never blocks the analyze flow.
    //
    // Phase 6.AK — Now uses the shared emitLifecycle() helper which:
    //   * uses fetch(...keepalive: true) so events survive navigation
    //   * falls back to navigator.sendBeacon() on fetch failure
    //   * automatically threads the per-attempt client_trace_id so the
    //     Owner Console can correlate the full action chain even when
    //     no backend request_id has been issued yet.
    const t0 = Date.now();
    const traceId = clientTraceIdRef.current;
    const logLifecycle = (eventName, extra = {}) => {
      emitLifecycle(eventName, {
        ticker: tk,
        request_id: localRequestId || extra.request_id || null,
        elapsed_ms: Date.now() - t0,
        user_email: user?.email || null,
        client_trace_id: traceId,
        // Phase 6.AJ — every lifecycle event carries the full ticker
        // provenance block so the Owner Console can reconstruct the
        // chain even from a single row.
        extra: { ...baseProvenance, ...extra },
      });
    };
    _lifeCycleEmit = logLifecycle;

    // Phase 6.AT — Recovery-from-cache helper.
    //
    // When a POST /analyze/{ticker} attempt fails with either:
    //   * HTTP 504 (backend's own pipeline timeout fired), or
    //   * a transport-layer error (axios got no HTTP response — e.g. the
    //     Kubernetes ingress proxy cut the connection at ~30-60s),
    // the backend MAY still complete the pipeline server-side and write
    // the result to `analysis_cache`. Polling
    // GET /api/analyze/{ticker}/cached for ~10s gives the in-flight
    // pipeline a chance to land its result. If the cache lookup
    // succeeds, we render the analysis just like a normal response.
    //
    // Returns:
    //   * true  — recovered, state has been updated to render the result.
    //   * false — all polls returned `found: false`. Caller should
    //             fall through to the regular timeout/error UI.
    //
    // Pure read-only: hits a GET endpoint that does NOT trigger any
    // FMP / LLM work and does NOT consume quota. Safe to call from any
    // error branch.
    const tryRecoverFromCache = async (ticker_up) => {
      const MAX_ATTEMPTS = 6;
      const DELAY_MS = 1600;
      logLifecycle("recovery_poll_started", {
        max_attempts: MAX_ATTEMPTS,
        delay_ms: DELAY_MS,
        caller: "recovery_poll",
        credential_source: (typeof window !== "undefined" && hasAuthHint())
          ? "bearer_token_present" : "cookie_possible",
        auth_request_sent_with_credentials: true,
      });
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
          const res = await api.get(`/analyze/${ticker_up}/cached`, { timeout: 8000 });
          const body = res?.data || {};
          // The endpoint returns `found: false` when no cached row
          // exists yet. Anything else (no `found` field at all, OR a
          // full payload with `ticker` / `metrics` / `valuation`) is a
          // recovery hit.
          if (body && body.found === false) {
            logLifecycle("recovery_poll_miss", {
              attempt,
              max_attempts: MAX_ATTEMPTS,
              backend_request_id: body.request_id || null,
            });
            // Wait then try again (skip wait on last iteration so we
            // exit promptly).
            if (attempt < MAX_ATTEMPTS) {
              await new Promise((r) => setTimeout(r, DELAY_MS));
            }
            continue;
          }
          // Got a payload. Treat it identically to a successful POST.
          logLifecycle("recovery_poll_hit", {
            attempt,
            recovered_from_cache: !!body.recovered_from_cache,
            analysis_status: body.analysis_status || null,
            data_source: body.data_source || null,
            extra: {
              attempt_id: attemptId,
              body_request_id: body?._meta_debug?.request_id || null,
            },
          });
          // Clear EVERY piece of stale state in one atomic-looking
          // burst. The result-wins useEffect below will also fire when
          // setData(body) commits, providing belt-and-suspenders, but
          // doing the clear here too means the UI flips to the result
          // state in the very same render pass.
          setReason(null);
          setAdminError(null);
          setLastRequestId(body?._meta_debug?.request_id || localRequestId);
          if (body.limited) {
            setLimited(true);
            // Phase 8.LP.2 — Same suppress-legacy-paywall behavior
            // in the recovery path.
            if (body?.access_level === "free_quota_exhausted_preview") {
              setData(body);
            } else {
              setPaywallOpen(true);
            }
          } else {
            setAnalyzeStage(null);  // no longer loading
            setAnalyzing(false);
            // Promote this attempt to the winning one BEFORE setData
            // so the render-time guard sees us as a success even if
            // React commits these in a single batch.
            latestSuccessAttemptIdRef.current = attemptId;
            setData(body);
          }
          // User-visible affordance — discreet toast so they know what
          // happened. No alarm, no scary copy.
          try {
            toast.success("Recovered cached result — analysis ready.", { duration: 4000 });
          } catch { /* swallow */ }
          return true;
        } catch (recoverErr) {
          // 401 / network error during recovery — give up immediately
          // and let the caller fall through to its existing error UI.
          // Don't retry past an auth failure.
          const recStatus = recoverErr?.response?.status;
          logLifecycle("recovery_poll_error", {
            attempt,
            status: recStatus || null,
            message: String(recoverErr?.message || "").slice(0, 200),
          });
          if (recStatus === 401 || recStatus === 403) return false;
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, DELAY_MS));
          }
        }
      }
      logLifecycle("recovery_poll_exhausted", { attempts: MAX_ATTEMPTS });
      return false;
    };

    if (!isRetry) {
      setAnalyzing(true);
      setAnalyzeStage("fetching");
      setLimited(false);
      setData(null);
      setReason(null);
      setAdminError(null);
      setReqId(null);
      logLifecycle("request_started", { is_retry: false });
    } else {
      logLifecycle("retry_started", { is_retry: true });
    }

    // Staged loading: advance UI copy on a timer so the user sees
    // meaningful progress even though we don't have server-sent events.
    // We DO NOT auto-abort the request — the frontend waits for the
    // backend to either return a result or a real error response.
    // (Previously a 90s AbortController was used; that has been removed
    //  per Phase 6.Y stabilization: production must never artificially
    //  abort a valid analysis. The browser/HTTP layer is still subject
    //  to its own intrinsic limits, but we do not impose any of our own.)
    const stageTimers = [];
    if (!isRetry) {
      stageTimers.push(setTimeout(() => setAnalyzeStage("valuation"),     4_500));
      stageTimers.push(setTimeout(() => setAnalyzeStage("ai_insights"),   6_000));
      stageTimers.push(setTimeout(() => setAnalyzeStage("finalizing"),   55_000));
    }

    const clearStageTimers = () => stageTimers.forEach(clearTimeout);

    try {
      // Phase 6.AI.5 — emit `fetch_started` BEFORE we send the POST so
      // the operator can see "the browser tried" even when no
      // http_audit_log row exists for the request. This is the key
      // forensic signal for the "frontend_timeout_no_backend_trace"
      // class of bug: if `fetch_started` exists but no audit_log row
      // exists for the same time window + ticker, the request never
      // reached the backend (wrong base URL, ingress drop, CSP block,
      // service-worker hijack, etc.).
      //
      // Phase 6.AJ — Ticker provenance is now automatically attached to
      // every lifecycle event via `baseProvenance`. We only need to add
      // the things that are specific to this exact event.
      // Phase 6.AK — Mark "fetch lifecycle started" so the timeout
      // screen telemetry can prove this attempt actually ran (and
      // distinguish a real timeout from a stale state contamination).
      didFetchStartRef.current = true;
      // Phase 6.AW — credential-source telemetry.
      //
      // The user reported production audit-log rows with `auth=none,
      // auth_diag=no_credential, bearer length=0` despite a visibly
      // logged-in navbar. Root cause was a CORS-spec violation that
      // made the browser silently drop cookies + Authorization on
      // cross-origin requests. To prove the fix and to catch any
      // future credential-stripping regression, every `fetch_started`
      // event now records EXACTLY what auth surfaces the frontend
      // INTENDS to send:
      //   * bearer_token_present — localStorage has a JWT-shaped
      //     token; axios interceptor will attach `Authorization`.
      //   * cookie_possible     — `withCredentials: true` on the
      //     shared `api` client; the browser MAY attach a
      //     `session_token` cookie if CORS allows it AND the cookie
      //     matches the request origin/domain.
      //   * none                — neither of the above. The optimistic
      //     proceed path will go out unauthenticated and we expect a
      //     401 from the backend.
      //
      // Correlated with the backend's `auth_diag` outcome on the same
      // request_id, the operator can pinpoint exactly where
      // credentials are being lost: present in the browser intent
      // (this event) but absent in the backend's diag => CORS/cookie
      // attachment failure. Present in both => credential was
      // genuinely invalid.
      const localToken = (typeof window !== "undefined")
        && hasAuthHint();
      const bearerTokenPresent = !!localToken;
      const credentialSource = bearerTokenPresent
        ? "bearer_token_present"
        : "cookie_possible";  // we always send withCredentials so cookie MAY be attached
      // NOTE: the local `logLifecycle` wrapper above spreads our extras
      // directly into the wire-level `extra` field, so we pass our
      // credential-source telemetry as TOP-LEVEL keys here (NOT nested
      // under `extra`) — otherwise they'd land at
      // `wire.extra.extra.credential_source`.
      logLifecycle("fetch_started", {
        endpoint: `/api/analyze/${tk}`,
        has_auth_token: bearerTokenPresent,
        credential_source: credentialSource,
        auth_request_sent_with_credentials: true,  // withCredentials is set on the shared api client
        caller: isRetry ? "retry" : "initial_analyze",
        attempt_id: attemptId,
      });
      // Phase 6.AK — Mark "backend POST issued". Set on the line
      // BEFORE the await so we capture the intent even if axios
      // throws synchronously (rare but possible on misconfigured
      // baseURL / interceptor exceptions).
      didBackendRequestStartRef.current = true;
      // Sub-pass 2B closure — attach the operation-level
      // Idempotency-Key. The helper preserves the same key for
      // transport retries of THIS logical operation and rotates
      // only when the user starts a NEW deliberate analysis.
      // Cleared on completion below.
      // eslint-disable-next-line global-require
      const { getOrCreateKey, clearKey, postAnalyzeWithIdempotency } = require("@/lib/analyzeIdempotency");
      const _idemKey = getOrCreateKey(tk);
      // Sub-pass 2B final closure — the helper wraps axios with a
      // bounded retry loop that honours HTTP 409 idempotency_in_progress
      // + Retry-After, reuses the SAME key across attempts, does NOT
      // retry on 422/409 idempotency_conflict, and clears the key on
      // terminal success.
      const res = await postAnalyzeWithIdempotency(api, tk, {
        idempotencyKey: _idemKey,
        params: simulatePlan ? { simulate_plan: simulatePlan } : undefined,
      });
      // Successful completion — retire the key so the next deliberate
      // analyze click for this ticker mints a fresh one.
      try { clearKey(tk); } catch (_) { /* noop */ }
      // Phase 6.AI.4 — Capture the X-Request-ID from the response headers
      // BEFORE doing anything else with the response. This becomes the
      // canonical request_id for every subsequent lifecycle event — the
      // SAME id that appears in `http_audit_log` server-side. Without
      // this, lifecycle events were uncorrelated with backend audit rows
      // and we couldn't trace the failing-MU bug.
      const hdrReqId =
        (res?.headers && (res.headers["x-request-id"] || res.headers.get?.("x-request-id"))) ||
        res?.data?._meta_debug?.request_id ||
        null;
      if (hdrReqId) setReqId(hdrReqId);
      logLifecycle("response_received", {
        ticker: tk,
        status: res?.status,
        limited: !!res.data?.limited,
        has_body: !!res?.data,
        cache_hit: !!res.data?.cache_hit,
        data_source: res.data?.data_source || null,
        analysis_status: res.data?.analysis_status || null,
        elapsed_ms: Date.now() - t0,
        // Phase 6.AI.6 — Ticker integrity check. If the ticker we asked
        // for doesn't match what the backend returned, that's a real
        // bug (likely a stale cached response or wrong route param).
        backend_returned_ticker: res.data?.ticker || null,
        ticker_mismatch:
          (res.data?.ticker || "").toUpperCase() !== tk,
      });

      // Phase 6.AL — RESPONSE-BODY DIAGNOSTIC TELEMETRY.
      // The user's P0 evidence: response_received fires with status=200
      // but the page renders "Something went wrong". That means
      // somewhere between `response_received` and `render_completed`,
      // the response gets rejected. The next three events tell the
      // Owner Console exactly where it goes wrong.
      // ----------------------------------------------------------------
      // (1) response_body_parsed — fingerprint the actual response body.
      //     This proves the body decoded as JSON and shows every
      //     top-level key + key payload field presence, so the operator
      //     can see whether the backend returned a structurally valid
      //     analyze payload, a sparse one, or an error-shaped 200.
      const body = res?.data || {};
      const bodyKeys = Object.keys(body || {});
      const metricsKeys = Object.keys(body?.metrics || {});
      const valuationKeys = Object.keys(body?.valuation || {});
      const aiKeys = Object.keys(body?.ai || {});
      const fingerprint = {
        top_keys: bodyKeys.slice(0, 30),
        has_metrics: !!body?.metrics,
        has_valuation: !!body?.valuation,
        has_ai: !!body?.ai,
        has_growth: !!body?.growth,
        metrics_key_count: metricsKeys.length,
        valuation_key_count: valuationKeys.length,
        ai_key_count: aiKeys.length,
        ticker_field: body?.ticker || null,
        analysis_status: body?.analysis_status || null,
        data_source: body?.data_source || null,
        data_source_quality: body?.data_source_quality || null,
        live_data_status: body?.live_data_status || null,
        ai_summary_status: body?.ai_summary_status || null,
        ai_full: !!body?.ai_full,
        cache_hit: !!body?.cache_hit,
        limited: !!body?.limited,
        render_safe_flag: body?._meta_debug?.frontend_render_safe ?? null,
        schema_valid_flag: body?._meta_debug?.schema_valid ?? null,
        backend_request_id: body?._meta_debug?.request_id || null,
      };
      logLifecycle("response_body_parsed", fingerprint);

      // (2) response_schema_validated — explicit check against the
      //     fields the React render path requires. We DO NOT crash on
      //     missing fields (the page is hardened against nulls in
      //     Phase 6.AH), but we DO log every absence so the operator
      //     can correlate the rendered error with a specific shape gap.
      const required = ["ticker", "metrics", "valuation"];
      const optional = ["ai", "growth", "alpha_signals"];
      const missing_required = required.filter(
        (k) => body?.[k] == null || (typeof body[k] === "object" && !Array.isArray(body[k]) && Object.keys(body[k]).length === 0 && k !== "ticker")
      );
      const missing_optional = optional.filter((k) => body?.[k] == null);
      const schema_valid = missing_required.length === 0;
      logLifecycle("response_schema_validated", {
        schema_valid,
        missing_required,
        missing_optional,
        analysis_status: body?.analysis_status || null,
      });

      // Phase 6.AI.3 — On ANY successful HTTP 200, force-clear every
      // stale error/timeout state. This is the bulletproof anti-race
      // contract: a backend 200 must always render as a successful
      // analyze, regardless of prior failed attempts in this React
      // tree's lifecycle. (`limited` is the only legitimate "200 but
      // not actually a result" payload — it carries the upgrade flow.)
      setReason(null);
      setAdminError(null);
      if (res.data?.limited) {
        setLimited(true);
        // Phase 8.LP.2 — Skip auto-opening the legacy PaywallModal
        // when we have a polished locked-preview payload: the UI
        // already presents the upgrade CTA prominently and we
        // don't want to stack two paywalls.
        if (res.data?.access_level === "free_quota_exhausted_preview") {
          setData(res.data);
        } else {
          setPaywallOpen(true);
        }
        logLifecycle("limited_response", { upgrade_required: true });
      } else {
        setAnalyzeStage("finalizing");
        // Phase 6.AL — RENDER LIFECYCLE EVENTS. Track the decision
        // point where the response is handed over to React for
        // rendering. If "Something went wrong" appears AFTER this
        // point, the bug is in render — log `render_failed` from the
        // error boundary. If it appears BEFORE this point, the bug
        // is in the response handling itself (the response_rejected
        // branch below).
        logLifecycle("render_started", {
          analysis_status: body?.analysis_status || null,
          will_show_data_unavailable: body?.analysis_status === "data_unavailable",
          schema_valid,
          missing_required,
        });
        setData(res.data);
        logLifecycle("response_accepted", {
          ticker: tk,
          analysis_status: res.data?.analysis_status || null,
          data_source: res.data?.data_source || null,
          cache_hit: !!res.data?.cache_hit,
          ai_summary_status: res.data?.ai_summary_status || null,
          had_stale_notFoundReason: !!localNotFoundReason,
          schema_valid,
        });
        // Phase 6.AL — Render confirmation. Scheduled with a 0ms
        // setTimeout so React commits the state update before this
        // fires. If `render_failed` lands first, the error boundary
        // intercepted before render completed; if `render_completed`
        // lands first, the analyze layout successfully painted.
        setTimeout(() => {
          try {
            logLifecycle("render_completed", {
              ticker: tk,
              analysis_status: res.data?.analysis_status || null,
              data_present_in_state: true,
            });
          } catch { /* swallow */ }
        }, 0);
      }
    } catch (e) {
      const status = e?.response?.status;
      const errDetail = e?.response?.data?.detail;
      // Backend now returns structured 500 errors with `stage` info.
      // Surface them to admins via toast; regular users see the generic
      // "not found / error" UI they used to get.
      const structured = (errDetail && typeof errDetail === "object") ? errDetail : null;
      const failingStage = structured?.stage;
      // Phase 6.AZ — Emit `analyze_error` lifecycle event the moment the
      // catch fires. This guarantees Owner Console sees a row for every
      // failed analyze even if the rest of the catch block bails out
      // (e.g. an identity-probe await throws). Fire-and-forget.
      try {
        emitLifecycle("analyze_error", {
          ticker: tk,
          request_id: structured?.request_id || e?.response?.headers?.["x-request-id"] || null,
          elapsed_ms: Date.now() - t0,
          user_email: user?.email || null,
          extra: {
            client_trace_id: clientTraceIdRef.current,
            http_status: status || null,
            failing_stage: failingStage || null,
            error_class: e?.name || null,
            error_message: (e?.message || String(e || "")).slice(0, 240),
            is_transport_error: !e?.response,
            stage_hint: structured?.stage_hint || null,
            backend_error_code: structured?.code || null,
          },
        });
      } catch { /* swallow */ }
      // Phase 6.AD — request_id is now returned in BOTH the response
      // header (X-Request-ID) AND the structured error detail. Use it
      // to cross-correlate frontend errors with the exact Mongo audit
      // row in db.http_audit_log.
      const requestId = (
        structured?.request_id ||
        e?.response?.headers?.["x-request-id"] ||
        null
      );
      // Stash for the "Something went wrong" surface (admin badge).
      setReqId(requestId);
      // Capture the actual URL axios called so admins can verify the
      // frontend is talking to the EXPECTED backend (preview vs prod).
      // e.config.baseURL + e.config.url is the resolved URL axios used.
      const calledUrl = (
        (e?.config?.baseURL || "") + (e?.config?.url || "")
      ) || "(unknown)";
      const expectedBackend = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "(unknown)";
      const backendUrlMissing = (typeof window !== "undefined") && !!window.__STOCKAILY_BACKEND_URL_MISSING__;
      // Best-effort identity probe — fire-and-forget so we don't block
      // the error UI. If the probe succeeds, we attach the backend's
      // self-reported identity to the admin error panel so the operator
      // can immediately see "I'm on prod frontend but hitting preview
      // backend" or "build version is X" without leaving the page.
      let probedIdentity = null;
      try {
        const probe = await api.get("/_health/identity", { timeout: 5000 });
        probedIdentity = probe?.data || null;
      } catch (probeErr) {
        probedIdentity = {
          probe_failed: true,
          message: probeErr?.message || "identity probe failed",
          status: probeErr?.response?.status || null,
        };
      }
      const isUnsupported = status === 400 && (
        (structured && structured.error === "unsupported_asset") ||
        (typeof errDetail === "string" && /unsupported_asset/i.test(errDetail))
      );
      // Phase 6.AC — detect requests that died at the transport layer
      // (no HTTP response). Most common production cause: Kubernetes
      // ingress proxy timeout fires before our backend responds, OR
      // the browser/network cancelled the request. We surface this to
      // admins so they can immediately see "the backend never got a
      // chance to reply" and check the audit log on the Owner Console.
      const isTransportError = !e?.response && !!e?.message;
      // Phase 6.AI.2 — When axios reports a transport error (no HTTP
      // response) AND this is the FIRST attempt, retry ONCE before
      // surfacing the error to the user. Rationale: in production the
      // backend frequently completes the analyze pipeline in 20s+ and
      // writes the result to analysis_cache, but the Kubernetes ingress
      // proxy occasionally tears down the client socket while keeping
      // the upstream connection alive (or in flight). Concretely this
      // manifests as `http_audit_log` showing HTTP 200 / outcome=ok /
      // duration=21235ms for the exact request that the user saw fail.
      //
      // The retry hits the freshly-populated `analysis_cache`, returns
      // in ~50ms, and the user never sees the spurious error. If the
      // backend genuinely failed, the cache will be empty and the
      // retry will hit the same path and fall through to the regular
      // error UI below.
      if (isTransportError && !isRetry) {
        // eslint-disable-next-line no-console
        console.warn(
          "[analyze.transport_error] backend connection dropped; retrying once to hit analysis_cache",
          { ticker: tk, message: e?.message, code: e?.code }
        );
        clearStageTimers();
        // Brief delay so any cache write that's still in-flight has time
        // to land. The analyze handler issues `set_analysis_cache` ~1ms
        // before returning, so 600ms is plenty.
        await new Promise((r) => setTimeout(r, 600));
        try {
          await runAnalyze({ isRetry: true });
          return; // success path — runAnalyze already updated state
        } catch (retryErr) {
          // eslint-disable-next-line no-console
          console.warn("[analyze.transport_error] retry also failed", retryErr);
          // fall through to the normal error UI below
        }
      }
      // Phase 6.AD — every admin error panel now carries a consistent
      // diagnostic context block: request_id, the URL axios actually
      // called, the expected backend URL from build-time env, and the
      // result of a pre-flight identity probe. This lets the operator
      // immediately see "I'm on prod frontend but hit preview backend"
      // or "the request_id is X — go grep db.http_audit_log".
      const adminDiag = {
        request_id: requestId,
        called_url: calledUrl,
        expected_backend: expectedBackend,
        backend_url_missing: backendUrlMissing,
        probed_identity: probedIdentity,
        http_status: status ?? null,
        window_location: (typeof window !== "undefined") ? window.location.origin : null,
      };
      const setAdminErrorWithDiag = (partial) => {
        setAdminError({ ...partial, diagnostic: adminDiag });
      };
      if (isUnsupported) {
        // Equity-only platform — show the dedicated "Unsupported asset" UI.
        setReason("unsupported_asset", { http_status: status });
      } else if (status === 404) {
        // Backend explicitly said this ticker doesn't exist (e.g. typo,
        // missing FMP coverage, mock fallback with no match).
        setReason("not_found", { http_status: 404 });
      } else if (status === 500 && structured?.code === "analyze_failed") {
        // Backend reached us but a sub-stage threw. Show the structured
        // failure so the user has context. For admins we also show the
        // exact stage in a separate debug toast — invaluable for hot
        // production debugging.
        setReason("error");
        const friendly = structured.message || "Analysis failed unexpectedly.";
        toast.error(friendly);
        if (user?.is_admin || user?.role === "admin") {
          // eslint-disable-next-line no-console
          console.error("[analyze.error]", structured, adminDiag);
          // Stash the structured payload so the inline admin panel below
          // can render the traceback. The backend includes `error_class`,
          // `error_message`, and (when admin auth was detected at the
          // backend) `traceback` so admins get a one-glance diagnosis.
          setAdminErrorWithDiag({
            stage: failingStage,
            error_class: structured.error_class,
            error_message: structured.error_message,
            traceback: structured.traceback,
            raw: structured,
          });
          toast.error(`[ADMIN] Failed at stage: ${failingStage} (req ${requestId || "?"})`, { duration: 8000 });
        }
      } else if (status === 504 && structured?.code === "analyze_timeout") {
        // Backend's own 55s budget fired. Pipeline took too long — show
        // a dedicated timeout UI with retry CTA. Admins get the exact
        // stage that overran.
        //
        // Phase 6.AT — RECOVERY POLL.
        // Before showing "Analysis took too long" we attempt to recover
        // the result from `analysis_cache`. The backend frequently
        // completes AFTER its own 504 budget hit (e.g. the LLM finishes
        // 1-2s late but the cache write still lands). Polling
        // /api/analyze/{ticker}/cached for ~10s gives the in-flight
        // pipeline a chance to finish + write, and renders the result
        // if found. If all polls miss, we fall through to the existing
        // timeout surface — no regression on the failure path.
        const recovered = await tryRecoverFromCache(tk);
        if (recovered) {
          clearStageTimers();
          return; // tryRecoverFromCache already set data + emitted lifecycle
        }
        setReason("timeout", {
          http_status: 504,
          backend_stage: structured?.stage,
          structured_code: structured?.code,
        });
        // Phase 6.AJ — explicit orphan-emit. Even though the backend DID
        // see the request (we got an HTTP 504 with a request_id), the
        // user-visible UX is "timeout screen" — log it with the full
        // url + ticker + reason so the operator can see the chain in
        // one row instead of cross-referencing.
        logLifecycle("frontend_timeout_no_backend_trace", {
          reason: "backend_504_timeout",
          had_backend_response: true,
          backend_stage: structured?.stage || null,
          elapsed_ms: structured?.elapsed_ms ?? null,
        });
        if (user?.is_admin || user?.role === "admin") {
          setAdminErrorWithDiag({
            stage: structured.stage,
            error_class: "PipelineTimeout",
            error_message: `Pipeline exceeded ${structured.budget_s}s budget (elapsed ${structured.elapsed_ms}ms). Last stage: ${structured.stage}.`,
            traceback: null,
            raw: structured,
          });
        }
      } else if (status === 503 && structured?.code === "force_live_unavailable") {
        // force_live diagnostic mode refused mock fallback.
        setReason("error");
        toast.error(structured.message || "Live data unavailable.");
        if (user?.is_admin || user?.role === "admin") {
          setAdminErrorWithDiag({
            stage: structured.stage,
            error_class: "ForceLiveUnavailable",
            error_message: structured.reason,
            traceback: null,
            raw: structured,
          });
        }
      } else if (isTransportError) {
        // No HTTP response at all — request was killed at the network
        // layer (browser navigation, ingress proxy timeout, DNS fail,
        // pod restart mid-flight). For admins this is critical to
        // diagnose because the backend's own telemetry won't show the
        // failing request body either.
        //
        // Phase 6.AT — RECOVERY POLL. The same backend pipeline that
        // got killed by an ingress proxy may still be running on the
        // backend and may have written its result to analysis_cache
        // 1-5s after the proxy cut the client off. Poll /cached for
        // ~10s before surfacing the network error so the user gets
        // their analysis when the backend recovered post-cutoff.
        const recoveredTx = await tryRecoverFromCache(tk);
        if (recoveredTx) {
          clearStageTimers();
          return;
        }
        setReason("error");
        toast.error("Network error — the analysis was interrupted. Please try again.");
        // Phase 6.AJ — explicit orphan-emit for transport failures.
        // This is the canonical signal that the browser timed out
        // WITHOUT any backend trace. Carries url + ticker + reason in
        // one row so the Owner Console "Frontend timeout — no backend
        // trace" panel can show it without a Mongo join.
        logLifecycle("frontend_timeout_no_backend_trace", {
          reason: "transport_error",
          had_backend_response: false,
          axios_code: e?.code || null,
          axios_message: (e?.message || "").slice(0, 240),
        });
        if (user?.is_admin || user?.role === "admin") {
          // eslint-disable-next-line no-console
          console.error("[analyze.transport_error]", e?.message, e?.code, adminDiag);
          setAdminErrorWithDiag({
            stage: "network",
            error_class: e?.code || "TransportError",
            error_message: (
              `${e?.message || "Request never received an HTTP response."} ` +
              "Most likely causes: Kubernetes ingress proxy timeout (60s), pod " +
              "restart mid-flight, browser-side abort, or production frontend " +
              "calling the WRONG backend URL. Compare 'called_url' vs " +
              "'expected_backend' below — and check 'probed_identity.host' to " +
              "confirm which backend actually answered the health probe."
            ),
            traceback: null,
            raw: { network: true, axios_code: e?.code, message: e?.message },
          });
          toast.error("[ADMIN] Transport failure — see diagnostic panel below", { duration: 8000 });
        }
      } else if (status === 401) {
        // Slice 1 (2026-02-14) — distinguish anonymous visitor from
        // a genuinely expired authenticated session.
        //
        // Root cause of the previous false "Session expired" toast:
        // this branch fired unconditionally on 401, even for a
        // visitor who never had a session (e.g. a logged-out user
        // clicking a ticker in the public search that lands on
        // /analyze/AAPL).  Backend correctly returns 401 to the
        // anonymous analyze POST, but the frontend then mis-messaged
        // it as an expired session.
        //
        // A previously-authenticated session is detected by the
        // presence of ANY of:
        //   * a live `user` object in AuthContext (server-verified),
        //   * the non-secret UI hint written after any successful
        //     login (setAuthHint / hasAuthHint — cookie-only auth
        //     also writes this hint via AuthContext.loginWith* and
        //     AuthCallback), or
        //   * a legacy JS-visible bearer credential (belt-and-braces
        //     — cleared elsewhere on 401 anyway).
        // Anything else means the visitor was never authenticated on
        // this browser and should see the marketing gate instead of
        // the "Session expired" surface.
        const hadAuthedSession = !!user || (typeof window !== "undefined" && hasAuthHint());
        if (!hadAuthedSession) {
          // Anonymous visitor — render the marketing gate.  No toast,
          // no scary copy.  Preserve the exact requested ticker in
          // the CTA URLs so signup/login continues here.
          setReason("auth_required", {
            http_status: 401,
            structured_code: structured?.code,
            was_ever_authed: false,
          });
        } else {
          // Phase 6.AR — DEDICATED auth-failure surface for a real
          // session that just expired.  Keeps every previously-shipped
          // behavior for authenticated users.
          setReason("auth_expired", {
            http_status: 401,
            structured_code: structured?.code,
            structured_detail: typeof errDetail === "string" ? errDetail : null,
            was_ever_authed: true,
          });
          toast.error(t("analyze.auth.session_expired_toast"), { duration: 6000 });
          // Best-effort: clear stale token + force AuthContext to re-fetch.
          try { clearAuthHint(); } catch { /* swallow */ }
          try { if (typeof setUser === "function") setUser(null); } catch { /* swallow */ }
          if (user?.is_admin || user?.role === "admin") {
            setAdminErrorWithDiag({
              stage: "auth",
              error_class: "NotAuthenticated",
              error_message:
                "Backend returned 401 with a bearer token attached. " +
                "Most likely: token expired, the production frontend is " +
                "calling the wrong backend (e.g., production sending its " +
                "token to the preview backend, which has a different DB), " +
                "or the auth secret differs between environments. " +
                "Check 'called_url' vs 'expected_backend' below — and verify " +
                "the request's Authorization header (Bearer prefix, length). " +
                "Inspect the new backend `analyze_auth_failures` panel on " +
                "the Owner Console for the exact rejection reason.",
              traceback: null,
              raw: structured,
            });
          }
        }
      } else if (status && status >= 400 && status < 500) {
        // Other client errors (e.g. malformed ticker, expired auth) —
        // surface as not-found since the user can't meaningfully recover
        // from them mid-flow. We DO stash an admin error hint for 401s
        // so admins can spot session-expiry issues.
        setReason("not_found");
        if (user?.is_admin || user?.role === "admin") {
          setAdminErrorWithDiag({
            stage: "client_error",
            error_class: `HTTP${status}`,
            error_message: typeof errDetail === "string" ? errDetail : JSON.stringify(structured || errDetail || {}),
            traceback: null,
            raw: structured,
          });
        }
      } else {
        setReason("error");
        toast.error(
          (structured && structured.message) ||
          (typeof errDetail === "string" ? errDetail : null) ||
          "Failed to analyze"
        );
        if (user?.is_admin || user?.role === "admin") {
          setAdminErrorWithDiag({
            stage: "unknown",
            error_class: "UnknownError",
            error_message: `axios catch reached an unexpected branch. status=${status ?? "none"} detail=${typeof errDetail === "string" ? errDetail.slice(0,200) : JSON.stringify(structured || {})}`,
            traceback: null,
            raw: { status, structured, errDetail },
          });
        }
      }
    } finally {
      clearStageTimers();
      // Phase 6.AI.2 — On a NON-render frontend failure (transport error,
      // pipeline timeout, 5xx, etc.) emit a `frontend_errors` row so the
      // Owner Console shows the exact ticker + request_id alongside the
      // backend audit row. Without this, transport-layer failures were
      // invisible to the operator unless a React component also crashed.
      //
      // Only fires on the OUTER (non-retry) attempt and only when the
      // user actually ends up seeing an error surface — not on the
      // happy path. We deliberately do NOT block the finally on the
      // POST (fire-and-forget) so the UI exit is instant.
      if (!isRetry && localNotFoundReason) {
        try {
          const backend = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
          const url = `${backend}/api/_health/frontend_error`;
          const body = {
            ticker: tk,
            boundary: "analyze_runtime",
            component: "Analyze.runAnalyze",
            error_name: "AnalyzeRunError",
            error_message: `notFoundReason=${localNotFoundReason} request_id=${localRequestId || "-"}`,
            error_stack: null,
            component_stack: null,
            user_agent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
            url: (typeof window !== "undefined") ? window.location.href : null,
            request_id: localRequestId || null,
            schema_valid: null,
            data_source_quality: null,
            ai_status: null,
          };
          // fire-and-forget — never block UI
          fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body),
          }).catch(() => { /* swallow */ });
        } catch { /* never let observability break the UX */ }
      }
      setAnalyzing(false);
      setAnalyzeStage(null);
    }
  };

  const refreshWatchlistStatus = async () => {
    try {
      const res = await api.get("/watchlist");
      setInWatchlist((res.data.items || []).some((it) => it.ticker === tk));
    } catch {
      setInWatchlist(false);
    }
  };

  const toggleWatchlist = async () => {
    if (!user) { navigate(`/login?next=/analyze/${tk}`); return; }
    try {
      if (inWatchlist) {
        await api.delete(`/watchlist/${tk}`);
        setInWatchlist(false);
        toast.success(`Removed ${tk} from watchlist`);
      } else {
        await api.post(`/watchlist`, { ticker: tk });
        setInWatchlist(true);
        toast.success(`Added ${tk} to watchlist`);
      }
    } catch (err) {
      // Handle the 402 watchlist limit case with an elegant upgrade prompt.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "watchlist_limit_reached") {
        toast.message("Watchlist limit reached", {
          description: detail.message || "Upgrade to Pro for unlimited watchlists and AI tracking.",
          action: {
            label: "Upgrade",
            onClick: () => navigate("/pricing"),
          },
          duration: 6000,
        });
        return;
      }
      toast.error("Watchlist update failed");
    }
  };

  // Phase 6.AH null-truthfulness — the backend response contract
  // guarantees `metrics`, `valuation`, and `ai` are dicts on a 200, but
  // we still defensively fall back to `{}` so that even a malformed
  // mid-flight cache row, a hand-crafted 200 from a future endpoint, or
  // a (theoretically impossible) post-contract mutation can't crash the
  // page on `m.market_cap` / `v.fair_value`. Empty objects render every
  // KPI as "Data unavailable" via the rawValue rule below.
  const m = data?.metrics || snap || {};
  const v = data?.valuation || {};
  const ai = data?.ai || {};
  const usage = data?.usage;

  // Auth gate
  // Phase 6.AQ — only block on the auth gate when we have NO TOKEN
  // AND no user. If we have a token, runAnalyze is firing under the
  // token-fallback path and we should render the analyze layout
  // (which will show its own loading / data / error states). This
  // fixes the "I'm logged in but Analyze sees no session" bug where
  // /auth/me timed out, user was null, but the token was still valid.
  //
  // Phase 6.AV — additional guard. Cookie-only authed users (Google
  // OAuth landed only an httponly cookie) have NO JS-visible auth
  // signal. Previously this gate hid the entire page from them. Now
  // we ONLY show the pre-emptive gate when AuthContext has
  // DEFINITIVELY confirmed the user is unauthed (via a real backend
  // 401, not a timeout). Otherwise we render the main analyze layout
  // and let the optimistic /api/analyze request adjudicate via the
  // cookie. The 401-driven `auth_expired` failure surface and
  // `stockaily:auth_failed` event keep the unauthed UX clean even if
  // the cookie ALSO fails.
  const hasStoredToken = (typeof window !== "undefined")
    && hasAuthHint();
    // WS-8: cookie-only auth — hint is a non-secret UI marker.
  if (!authLoading && !user && !hasStoredToken && authProbedUnauthed) {
    // Slice 1 (2026-02-14) — anonymous visitors reaching a protected
    // analysis route see the conversion-friendly marketing gate.
    // Ticker is preserved through both CTAs via ?next=/analyze/{TK}.
    // No toast is fired here; no "Session expired" copy.
    return (
      <div className="min-h-screen flex flex-col" data-testid="analyze-auth-gate">
        <NavBar />
        <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <AnonymousAnalyzeGate ticker={tk} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col analyze-page-shell">
      <NavBar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Phase 4 — Graceful "Stock not found" / timeout / error UI.
            Renders in place of the analyze layout so we never get stuck on
            a perpetual loading spinner if the ticker is invalid or the
            backend stalls.

            Phase 6.AI.3 — CRITICAL: `data` wins over `notFoundReason`.
            If a previous request set notFoundReason="timeout" and a
            subsequent (or retry) request landed a 200 with payload,
            we MUST render the analysis. Showing the timeout screen on
            top of valid data was the production failure mode: the
            backend returns 200 OK / 22270ms / cache_hit, but the
            frontend still rendered "Analysis took too long" because
            the timeout state was stale.

            The new invariant: notFoundReason can only show when data
            is genuinely absent. If both are set, data wins.

            Phase 6.AP — ABSOLUTE BACKSTOP. Even if some new code path
            somehow set notFoundReason="timeout" without a fetch having
            been issued (impossible by inspection but defense-in-depth),
            we REFUSE to render "Analysis took too long" when
            `didFetchStartRef.current === false`. A timeout requires a
            fetch by definition. Instead we render the explicit
            "Waiting for session" / "Login required" state below. */}
        {(() => {
          // Absolute backstop — see comment above.
          const isImpossibleTimeout =
            notFoundReason === "timeout" && !didFetchStartRef.current;
          if (isImpossibleTimeout) {
            // Emit telemetry once so the Owner Console can prove this
            // protection fired. Then fall through to the auth-loading
            // surface (which will show "Waiting for session" /
            // "Login required" instead of a misleading timeout).
            try {
              if (typeof window !== "undefined") {
                if (!window.__STOCKAILY_BACKSTOP_FIRED__) {
                  window.__STOCKAILY_BACKSTOP_FIRED__ = {};
                }
                if (!window.__STOCKAILY_BACKSTOP_FIRED__[tk]) {
                  window.__STOCKAILY_BACKSTOP_FIRED__[tk] = true;
                  emitLifecycle("impossible_timeout_suppressed", {
                    ticker: tk,
                    client_trace_id: clientTraceIdRef.current,
                    extra: {
                      auth_loading: !!authLoading,
                      has_user: !!user,
                      has_token: (typeof window !== "undefined")
                        && hasAuthHint(),
                      previous_ticker: previousTickerRef.current,
                      pathname: window.location.pathname,
                    },
                  });
                }
              }
            } catch { /* swallow */ }
          }
          // Phase 6.AP — Explicit waiting-for-session surface.
          // When auth is still loading AND no fetch has started, the
          // user should see a clear "Waiting for session" message,
          // NOT a stale timeout or a misleading skeleton.
          if (authLoading && !didFetchStartRef.current && !data) {
            return (
              <div
                className="mx-auto mt-8 sm:mt-12 max-w-xl text-center"
                data-testid="analyze-waiting-for-session"
              >
                <div className="rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-10 shadow-[var(--app-shadow-elev-1)]">
                  <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-card border border-border/70 mb-3">
                    <span className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
                  </div>
                  <h1 className="font-display text-2xl tracking-tight">{t("analyze.session.waiting_title")}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t("analyze.session.waiting_body", { ticker: tk })}
                  </p>
                </div>
              </div>
            );
          }
          // Original failure surfaces. The impossible_timeout case
          // also flows here but will be filtered by `notFoundReason
          // && !data && !isImpossibleTimeout`.
          //
          // Phase 6.AU — RESULT-WINS render guard.
          //
          // Three additional safeguards layered on top of the original
          // `!data` check:
          //   1. `!data?.ticker` — even if `data` is somehow a sentinel
          //      truthy value (empty object, partial mid-write), require
          //      an actual ticker field before treating it as "result
          //      present". The render path downstream gates on data.ticker.
          //   2. Refuse to render the timeout screen when a successful
          //      attempt has been recorded (`latestSuccessAttemptIdRef >
          //      0`). This catches the "stale state from a previous
          //      attempt that the result-wins effect hasn't cleared yet"
          //      race that the user reported.
          //   3. Emit `timeout_screen_shown` lifecycle the first time
          //      this branch actually renders, so the operator can see
          //      from telemetry whether the screen ever appeared and
          //      for how long.
          const hasValidResult = !!(data && data.ticker);
          const hasPriorSuccess = latestSuccessAttemptIdRef.current > 0;
          const shouldShowFailure =
            !!notFoundReason
            && !hasValidResult
            && !hasPriorSuccess
            && !isImpossibleTimeout;
          if (!shouldShowFailure && hasValidResult && notFoundReason) {
            // We're rendering data, but notFoundReason is still set.
            // The result-wins effect WILL clear it on next tick — but
            // for THIS render we want the visible state to be "result".
            // No-op here; the JSX below this IIFE returns the main
            // analyze layout because we don't enter the `if`.
          }
          if (shouldShowFailure) {
            // Record (idempotently) when the timeout screen first
            // appeared, so the result-wins effect can compute "how long
            // was this visible?" when it clears.
            if (timeoutScreenShownAtRef.current === null) {
              timeoutScreenShownAtRef.current = Date.now();
              try {
                emitLifecycle("timeout_screen_shown", {
                  ticker: tk,
                  reason: notFoundReason,
                  attempt_id: currentAttemptIdRef.current,
                  request_id: lastRequestId,
                  extra: {
                    has_prior_success: hasPriorSuccess,
                    is_impossible_timeout: isImpossibleTimeout,
                  },
                });
              } catch { /* swallow */ }
            }
            return (
          <>
            {/* Phase 6.AI.3 — Diagnostic emit: log to backend WHEN this
                surface renders, so we can prove (in db.frontend_lifecycle)
                that the timeout screen showed despite ANY backend
                response that did or didn't arrive. */}
            <TimeoutScreenTelemetry
              ticker={tk}
              reason={notFoundReason}
              requestId={lastRequestId}
              hasData={!!data}
              userAgent={typeof navigator !== "undefined" ? navigator.userAgent : null}
              clientTraceId={clientTraceIdRef.current}
              didFetchStart={didFetchStartRef.current}
              didBackendRequestStart={didBackendRequestStartRef.current}
              routeTicker={ticker}
              typedTicker={typedTickerRef.current}
              previousTicker={previousTickerRef.current}
              authState={authLoading ? "loading" : (user ? "authed" : "unauthed")}
            />
            {notFoundReason === "auth_required" ? (
              /* Slice 1 (2026-02-14) — anonymous-gate surface for a
                 logged-out visitor who reached /analyze/{ticker}
                 directly.  Replaces the technical StockNotFound
                 surface with conversion-friendly copy and preserves
                 the requested ticker through the Login / Signup /
                 Google-callback return path. */
              <AnonymousAnalyzeGate ticker={tk} />
            ) : (
              <StockNotFound
                ticker={tk}
                reason={notFoundReason}
                requestId={lastRequestId}
                isAdmin={!!(user?.is_admin || user?.role === "admin")}
              />
            )}
            {adminError && (user?.is_admin || user?.role === "admin") ? (
              <div
                className="mt-6 mx-auto max-w-3xl rounded-xl border border-rose-500/40 bg-rose-500/5 p-4"
                data-testid="admin-error-panel"
              >
                <div className="flex items-start gap-2 mb-2">
                  <div className="text-rose-600 font-semibold text-sm">
                    Admin debug — analyze failed
                  </div>
                  <div className="ml-auto text-[10px] uppercase tracking-wider text-rose-700/70">
                    visible to admins only
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs mb-3">
                  <div>
                    <span className="text-muted-foreground">Stage:</span>{" "}
                    <code className="text-[11px]">{adminError.stage || "—"}</code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Error:</span>{" "}
                    <code className="text-[11px]">{adminError.error_class || "—"}</code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ticker:</span>{" "}
                    <code className="text-[11px]">{tk}</code>
                  </div>
                </div>
                {adminError.error_message ? (
                  <div className="text-xs mb-2">
                    <div className="text-muted-foreground mb-0.5">Message</div>
                    <pre className="whitespace-pre-wrap break-all bg-black/40 text-amber-100 p-2 rounded text-[11px] max-h-32 overflow-auto" data-testid="admin-error-message">
                      {adminError.error_message}
                    </pre>
                  </div>
                ) : null}
                {adminError.traceback ? (
                  <div className="text-xs">
                    <div className="text-muted-foreground mb-0.5">Traceback (last 1.5kb)</div>
                    <pre className="whitespace-pre-wrap break-all bg-black/60 text-rose-200 p-2 rounded text-[10px] max-h-72 overflow-auto" data-testid="admin-error-traceback">
                      {adminError.traceback}
                    </pre>
                  </div>
                ) : null}
                {/* Phase 6.AD — wire-level diagnostic block. Always shown
                    on admin error panel so the operator can immediately
                    verify which backend was called, what request_id to
                    grep in db.http_audit_log, and whether the production
                    frontend is talking to the expected production backend.
                */}
                {adminError.diagnostic ? (
                  <div className="text-xs mt-3 pt-3 border-t border-rose-500/20" data-testid="admin-error-diag">
                    <div className="text-muted-foreground mb-1.5 font-semibold">Wire diagnostic</div>
                    {adminError.diagnostic.backend_url_missing ? (
                      <div className="mb-2 p-2 rounded bg-rose-600/15 border border-rose-600/40 text-[11px] text-rose-800 font-semibold" data-testid="admin-error-backend-missing">
                        ⚠ REACT_APP_BACKEND_URL is empty in this build. The frontend is calling its own origin instead of a configured backend. Fix the deploy environment variable and rebuild the frontend.
                      </div>
                    ) : null}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                      <div>
                        <span className="text-muted-foreground">request_id:</span>{" "}
                        <code className="text-[11px] text-amber-700" data-testid="admin-error-request-id">
                          {adminError.diagnostic.request_id || "(not returned)"}
                        </code>
                      </div>
                      <div>
                        <span className="text-muted-foreground">http status:</span>{" "}
                        <code className="text-[11px]">{adminError.diagnostic.http_status ?? "no response"}</code>
                      </div>
                      <div className="md:col-span-2">
                        <span className="text-muted-foreground">called URL:</span>{" "}
                        <code className="text-[11px] break-all" data-testid="admin-error-called-url">
                          {adminError.diagnostic.called_url}
                        </code>
                      </div>
                      <div className="md:col-span-2">
                        <span className="text-muted-foreground">expected backend:</span>{" "}
                        <code className="text-[11px] break-all">{adminError.diagnostic.expected_backend}</code>
                      </div>
                      <div className="md:col-span-2">
                        <span className="text-muted-foreground">window origin:</span>{" "}
                        <code className="text-[11px] break-all">{adminError.diagnostic.window_location || "—"}</code>
                      </div>
                    </div>
                    {adminError.diagnostic.probed_identity ? (
                      <div className="mt-2">
                        <div className="text-muted-foreground mb-0.5 text-[11px]">
                          Identity probe response (GET /api/_health/identity)
                        </div>
                        <pre className="whitespace-pre-wrap break-all bg-black/40 text-emerald-200 p-2 rounded text-[10px] max-h-48 overflow-auto" data-testid="admin-error-probe">
                          {JSON.stringify(adminError.diagnostic.probed_identity, null, 2)}
                        </pre>
                        {adminError.diagnostic.probed_identity?.host ? (
                          (() => {
                            const probedHost = adminError.diagnostic.probed_identity?.host || "";
                            const windowHost = (typeof window !== "undefined" && window.location?.hostname) || "";
                            const mismatch =
                              !!probedHost && !!windowHost &&
                              !probedHost.includes(windowHost.split(".").slice(-3).join(".")) &&
                              !windowHost.includes(probedHost.split(".").slice(-3).join("."));
                            return mismatch ? (
                              <div className="mt-2 text-[11px] font-semibold text-rose-700" data-testid="admin-error-host-mismatch">
                                ⚠ Frontend origin ({windowHost}) and backend probe host ({probedHost}) don't share a domain. The production frontend may be calling the WRONG backend.
                              </div>
                            ) : null;
                          })()
                        ) : null}
                      </div>
                    ) : null}
                    <div className="mt-2 text-[10px] text-muted-foreground">
                      Tip: grep <code>request_id={adminError.diagnostic.request_id || "?"}</code> in <code>db.http_audit_log</code> to see exactly what the backend recorded (or didn't).
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
            );
          }
          // Fall through — render the main analyze layout.
          return (
        <>
        {/* Phase 6.AU — analysis_component_mounted sentinel.
            Emits the `analysis_component_mounted` lifecycle event the
            moment the main analyze layout commits to the DOM. This is
            the canonical "the user is actually seeing the analysis"
            signal — distinct from `render_completed` which fires from
            a setTimeout(0) that can race ahead of the actual commit. */}
        <AnalysisComponentMounted
          ticker={tk}
          hasData={!!(data && data.ticker)}
          recoveredFromCache={!!data?.recovered_from_cache}
          analysisStatus={data?.analysis_status || null}
          attemptId={currentAttemptIdRef.current}
          data={data}
          userEmail={user?.email || null}
        />
        {/* Phase 6.AY — Admin-only LIVE_DATA_ONLY violation banner.
            When the backend used mock data while LIVE_DATA_ONLY is
            active, this analysis is INVALID and must be marked as such
            for owner/admin users. Normal users never see this — they get
            the standard "Data temporarily unavailable" surface instead
            (handled separately above by the data_unavailable path). */}
        {(user?.is_admin || user?.role === "admin")
          && data?._meta_debug?.provenance?.live_data_only_violation ? (
          <div
            className="mt-4 rounded-lg border border-rose-500/50 bg-rose-500/10 p-4 text-rose-700"
            role="alert"
            data-testid="live-data-only-violation-banner"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <div className="font-semibold mb-1">
                  Invalid live result: mock data was used while LIVE_DATA_ONLY is active.
                </div>
                <div className="text-[12px] opacity-90">
                  {data._meta_debug.provenance.live_data_only_violation_reason
                    || "This analysis is not trustworthy and is being shown to admins only for diagnostic purposes."}
                </div>
                <div className="mt-1 text-[11px] opacity-70 font-mono">
                  analysis_id={data._meta_debug.provenance.analysis_id || "?"}
                  {" · "}request_id={data._meta_debug.provenance.request_id || "?"}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {/* Compact ticker header */}
        {/* Phase 8.LP.POLISH — Unified Analyze Hero Header.
            Consolidates what were previously TWO separate blocks
            (a legacy ticker/price/badges bar + a standalone
            identity header) into ONE CompanyIdentityHeader with
            slot props for price, badges, and right-side actions.
            Values are otherwise unchanged. Mode badge +
            ProvenancePopover remain admin-only. */}
        {data && data.company_identity && data.access_level !== "free_quota_exhausted_preview" && (
          <div data-testid="analyze-company-identity-wrapper">
            <CompanyIdentityHeader
              identity={data.company_identity}
              price={m ? `$${fmtPrice(m.price)}` : null}
              badges={[
                v ? <StatusBadge key="status" status={v.status} classification={v.classification} /> : null,
                v && (user?.is_admin || user?.role === "admin") ? (
                  <span
                    key="mode"
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${modeColor(v.mode)}`}
                    data-testid="stock-mode-chip"
                  >
                    Mode: {v.mode}
                  </span>
                ) : null,
                data ? <LiveDataChip key="live" data={data} /> : null,
                data && (user?.is_admin || user?.role === "admin") ? (
                  <ProvenancePopover key="prov" data={data} visible={true} />
                ) : null,
              ]}
              rightSlot={
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleWatchlist}
                    data-testid="analyze-toggle-watchlist"
                  >
                    {inWatchlist ? (
                      <>
                        <StarOff className="w-4 h-4 mr-1" />
                        {t("analyze.btn_remove")}
                      </>
                    ) : (
                      <>
                        <Star className="w-4 h-4 mr-1" />
                        {t("analyze.btn_watchlist")}
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={runAnalyze}
                    disabled={analyzing}
                    data-testid="analyze-rerun-button"
                  >
                    <RefreshCcw className="w-4 h-4 mr-1" />
                    {t("analyze.btn_reanalyze")}
                  </Button>
                </>
              }
            />
            {/* Admin-only QA Simulation banner — same admin
                gating as the Mode chip. Kept OUT of the identity
                header's badges slot because it's a wider banner
                that reads better as a full-row element. */}
            {data?.admin_simulated_plan ? (
              <div
                className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11.5px] text-emerald-700 dark:text-emerald-300 flex flex-wrap items-center gap-x-3 gap-y-1"
                data-testid="admin-qa-simulation-banner"
              >
                <span className="font-medium">
                  <ShieldCheck className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                  QA Simulation Active
                </span>
                <span>
                  simulated_plan:&nbsp;
                  <code className="font-mono">{data.user_plan || "—"}</code>
                </span>
                <span>
                  real_account_plan:&nbsp;
                  <code className="font-mono">{data.real_account_plan || "—"}</code>
                </span>
                <span>
                  usage_counted:&nbsp;
                  <code className="font-mono">{String(data.usage_counted ?? "—")}</code>
                </span>
                <span>
                  business_kpi_counted:&nbsp;
                  <code className="font-mono">{String(data.business_kpi_counted ?? "—")}</code>
                </span>
              </div>
            ) : null}
            {/* Company Profile accordion — hidden for locked
                preview state (its CTA is the primary focus
                there). */}
            <div className="mt-4">
              <CompanyProfileAccordion identity={data.company_identity} />
            </div>
          </div>
        )}

        {/* Legacy header — kept ONLY as a fallback for the pre-
            8.LP payload shape (no ``company_identity`` block, e.g.
            a very old cached row served before identity migration).
            This branch retains the previous behavior verbatim so
            no user is ever left with a page that has no header at
            all. */}
        {data && data.access_level !== "free_quota_exhausted_preview" && !data.company_identity && (
          <div className="flex items-start sm:items-center gap-4 flex-col sm:flex-row sm:justify-between" data-testid="analyze-header">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-secondary/60 border border-border/70 flex items-center justify-center font-mono">{tk}</div>
              <div className="min-w-0">
                <div className="text-sm text-muted-foreground truncate">{m?.name || "Loading…"}</div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h1 className="font-display text-2xl sm:text-3xl tracking-tight">{tk}</h1>
                  {m ? <span className="font-mono tabular text-2xl sm:text-3xl">${fmtPrice(m.price)}</span> : null}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {v ? <StatusBadge status={v.status} classification={v.classification} /> : null}
                  {v && (user?.is_admin || user?.role === "admin") ? (
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${modeColor(v.mode)}`} data-testid="stock-mode-chip">Mode: {v.mode}</span>
                  ) : null}
                  {m?.sector ? <Badge variant="outline" className="text-[10px]">{m.sector}</Badge> : null}
                  {data ? <LiveDataChip data={data} /> : null}
                  {data && (user?.is_admin || user?.role === "admin") ? (
                    <ProvenancePopover data={data} visible={true} />
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={toggleWatchlist} data-testid="analyze-toggle-watchlist">
                {inWatchlist ? <><StarOff className="w-4 h-4 mr-1" /> {t("analyze.btn_remove")}</> : <><Star className="w-4 h-4 mr-1" /> {t("analyze.btn_watchlist")}</>}
              </Button>
              <Button size="sm" onClick={runAnalyze} disabled={analyzing} data-testid="analyze-rerun-button"><RefreshCcw className="w-4 h-4 mr-1" /> {t("analyze.btn_reanalyze")}</Button>
            </div>
          </div>
        )}

        {/* Loading */}
        {(analyzing || (!data && !limited)) && (
          <div className="mt-6"><ScanLoader ticker={tk} stage={analyzeStage} /></div>
        )}

        {/* Phase 8.LP.2 — Locked preview for Free quota-exhausted
            users. Detected by the server-side ``access_level`` flag
            (never inferred from CSS). When present, we render the
            locked-cards + upgrade CTA IN PLACE OF the full
            analysis. The legacy ``limited`` banner is suppressed
            when access_level is present to avoid stacking two
            paywall UIs. Company identity (if any) is rendered
            above the locked preview so quota-exhausted users still
            get the polished header experience. */}
        {data && data.access_level === "free_quota_exhausted_preview" && (
          <div className="mt-6" data-testid="analyze-locked-preview-wrapper">
            {data.company_identity && (
              <CompanyIdentityHeader
                identity={data.company_identity}
                className="mb-6"
              />
            )}
            <LockedPreviewCard data={data} />
          </div>
        )}

        {/* Legacy limited banner — kept for backward compatibility
            with older backend deployments that still return only
            ``{limited: true, message}`` without ``access_level``.
            Suppressed automatically when the new locked-preview UI
            takes over. */}
        {limited && !(data && data.access_level === "free_quota_exhausted_preview") && (
          <div className="mt-6 rounded-xl border border-border/70 bg-card/60 p-5 flex items-start gap-3" data-testid="analyze-limit-banner">
            <Lock className="w-5 h-5 mt-0.5 text-[#3182CE]" />
            <div className="flex-1">
              <div className="font-medium">{t("analyze.limit.title")}</div>
              <div className="text-sm text-muted-foreground">{t("analyze.limit.subtitle")}</div>
            </div>
            <Button onClick={() => navigate("/billing")} data-testid="analyze-upgrade-cta">{t("analyze.limit.upgrade_cta")}</Button>
          </div>
        )}

        {/* Phase 6.AI — Clean "Data unavailable" surface. The backend now
            returns HTTP 200 with `analysis_status: "data_unavailable"`
            when FMP cannot supply usable fundamentals (rate-limited,
            plan-restricted, ticker not covered, etc.). We render a
            dedicated honest surface here INSTEAD of the regular analysis
            block — but still keep the page premium and offer the user a
            clear retry path. The full analyze layout is suppressed so we
            never show fake $0 / "—" / 0% tiles next to a real-looking
            "AI Opportunity Score" while honest fundamentals are missing. */}
        {data && data.analysis_status === "data_unavailable" && (
          <div
            data-testid="analyze-data-unavailable"
            className="mt-6 mx-auto max-w-3xl"
          >
            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 sm:p-8 shadow-[var(--app-shadow-elev-1)]">
              <div className="flex flex-col items-center text-center">
                <div
                  className="inline-flex w-14 h-14 items-center justify-center rounded-2xl mb-4"
                  style={{ background: "rgba(224,179,65,0.12)", border: "1px solid rgba(224,179,65,0.32)", color: "#E0B341" }}
                  aria-hidden
                >
                  <AlertCircle className="w-7 h-7" />
                </div>
                <h1 className="font-display text-2xl sm:text-3xl tracking-tight">
                  {t("analyze.unavailable.title", { ticker: tk })}
                </h1>
                <p className="mt-2 text-sm sm:text-[15px] text-muted-foreground leading-relaxed max-w-md" data-testid="analyze-data-unavailable-message">
                  {data.unavailable_message || t("analyze.unavailable.message_fallback")}
                </p>
                {(user?.is_admin || user?.role === "admin") && data?._meta_debug?.request_id ? (
                  <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-700">
                    <span className="opacity-70">request_id</span>
                    <code className="font-mono" data-testid="analyze-data-unavailable-request-id">{data._meta_debug.request_id}</code>
                  </div>
                ) : null}
              </div>
              <div className="mt-6 flex justify-center gap-2">
                <Button size="sm" onClick={runAnalyze} disabled={analyzing} data-testid="analyze-data-unavailable-retry">
                  <RefreshCcw className="w-4 h-4 mr-1" /> {t("analyze.unavailable.try_again")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/")} data-testid="analyze-data-unavailable-home">
                  {t("analyze.unavailable.pick_different")}
                </Button>
              </div>
              {(user?.is_admin || user?.role === "admin") ? (
                <div className="mt-5 pt-4 border-t border-amber-500/20" data-testid="analyze-data-unavailable-admin-diag">
                  <AdminAnalyzeDiagnostic data={data} />
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Result */}
        {data && data.analysis_status !== "data_unavailable" && data.access_level !== "free_quota_exhausted_preview" && (
          <AnalyzeErrorBoundary
            ticker={tk}
            isAdmin={!!(user?.is_admin || user?.role === "admin")}
            requestId={data?._meta_debug?.request_id || lastRequestId}
            schemaValid={data?._meta_debug?.schema_valid}
            dataSourceQuality={data?.data_source_quality}
            aiStatus={data?.ai_summary_status}
          >
          <div className="mt-6 space-y-6">
            {/* Phase 6.AF — admin-only diagnostic strip. Shows
                data_source_quality, FMP endpoint outcomes for THIS
                analyze, AI status, and a "data sufficiency" warning when
                completeness is low. Hidden entirely from regular users. */}
            {(user?.is_admin || user?.role === "admin") ? (
              <AdminAnalyzeDiagnostic data={data} />
            ) : null}

            {/* Phase 6.AF — user-visible (non-admin) banner when the
                fundamentals were too sparse for the analysis to be
                meaningful. Shown when data_completeness <= 0.2 OR when
                ALL key endpoints failed. Prevents users from seeing a
                "$0 fair value · 0% confidence" page without context. */}
            {(() => {
              const dc = data?.metrics?.data_completeness;
              const dsq = data?.data_source_quality;
              const epFailed = data?.endpoints_status?.failed_count || 0;
              const epOk = data?.endpoints_status?.ok_count || 0;
              const isSparse = (
                (dc != null && dc <= 0.2) ||
                (epFailed > 0 && epOk <= 1) ||
                dsq === "mock"
              );
              if (!isSparse) return null;
              return (
                <div
                  className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3"
                  data-testid="analyze-insufficient-data-banner"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <Info className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="text-sm leading-relaxed">
                    <div className="font-semibold text-amber-700 mb-0.5" data-testid="analyze-insufficient-data-title">
                      {t("analyze.sparse.title", { ticker: tk })}
                    </div>
                    <div className="text-muted-foreground">
                      {t("analyze.sparse.body")}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* HERO: huge AI Score + headline + price/fair-value */}
            {/* Feb 2026 — Valuation-safety unavailable banner.
                Renders ABOVE the ScorePanel when the deterministic
                pipeline returned an explicit UNAVAILABLE record
                (proven currency/share-unit mismatch OR unverified
                extreme outlier).  The ScorePanel itself is now
                Unavailable-aware and renders "—" for every numeric
                field, so we do NOT hide it (it still surfaces the
                unit-safe quality/growth/safety sub-scores which
                remain useful business context).  The banner
                clarifies to the user exactly WHY the valuation
                fields are blank, using the backend-provided
                truthful reason. */}
            {(v?.status === "Unavailable" || v?.signal === "Unavailable") ? (
              <div
                className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 sm:p-5"
                data-testid="analyze-valuation-unavailable-banner"
                data-valuation-unit-status={v?.valuation_unit_status || ""}
                data-outlier-status={v?.outlier_status || ""}
              >
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="w-5 h-5 mt-0.5 shrink-0"
                    style={{ color: "#E0B341" }}
                    aria-hidden
                  />
                  <div className="text-sm">
                    <div
                      className="font-medium text-foreground"
                      data-testid="analyze-valuation-unavailable-title"
                    >
                      {v?.outlier_status === "unverified"
                        ? t("analyze.valuation_unavailable.title_outlier", { ticker: tk })
                        : t("analyze.valuation_unavailable.title_unreconciled", { ticker: tk })}
                    </div>
                    <div
                      className="mt-1 text-muted-foreground leading-relaxed"
                      data-testid="analyze-valuation-unavailable-message"
                    >
                      {v?.user_message
                        || v?.unavailable_reason
                        || v?.valuation_unit_reason
                        || v?.outlier_reason
                        || (v?.outlier_status === "unverified"
                          ? t("analyze.valuation_unavailable.message_outlier_fallback")
                          : t("analyze.valuation_unavailable.message_unreconciled_fallback"))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <SectionErrorBoundary section="score_panel" label="Score panel" ticker={tk} isAdmin={!!(user?.is_admin || user?.role === "admin")}>
                <ScorePanel valuation={v} price={m.price} animateKey={tk} />
              </SectionErrorBoundary>
            </motion.div>

            {/* INSTITUTIONAL SNAPSHOT — Phase 1 deterministic engine overlay.
                 Renders only when the backend returned an `institutional`
                 block with `available: true`; degrades gracefully otherwise. */}
            {v?.institutional?.available ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.04 }}
              >
                <SectionErrorBoundary section="institutional_snapshot" label="Institutional snapshot" ticker={tk} isAdmin={!!(user?.is_admin || user?.role === "admin")}>
                  <InstitutionalSnapshot institutional={v.institutional} valuation={v} />
                </SectionErrorBoundary>
              </motion.div>
            ) : null}

            {/* CHART (above the fold for desktop) */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.06 }}>
              <SectionErrorBoundary section="price_chart" label="Price chart" ticker={tk} isAdmin={!!(user?.is_admin || user?.role === "admin")}>
                <PriceChart ticker={tk} fairValue={v.fair_value} fairLow={v.fair_value_low} fairHigh={v.fair_value_high} valuation={v} height={420} />
              </SectionErrorBoundary>
            </motion.div>

            {/* AI Alpha Signals — premium-feel institutional intelligence
                 (replaces the plain Pro teaser). Free users see partial
                 previews with the rest blurred; Pro users see full reasoning. */}
            <motion.div id="pro-insights" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.10 }}>
              <SectionErrorBoundary section="alpha_signals" label="Alpha signals" ticker={tk} isAdmin={!!(user?.is_admin || user?.role === "admin")}>
                <AlphaSignals
                  ticker={tk}
                  aiFull={!!data.ai_full}
                  signals={data.alpha_signals || []}
                />
              </SectionErrorBoundary>
            </motion.div>

            {/* Two-column: Insights + Drivers */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }} className="lg:col-span-7">
                <SectionErrorBoundary section="ai_insights_panel" label="AI insights" ticker={tk} isAdmin={!!(user?.is_admin || user?.role === "admin")}>
                  <AIInsightsPanel
                    ai={ai}
                    narrative={v?.institutional?.narrative}
                    full={!!data.ai_full}
                    disclaimer={data.disclaimer}
                    ticker={tk}
                    summaryStatus={data.ai_summary_status}
                    narrativeStatus={data.narrative_status}
                    structuredAiSanitized={!!data.structured_ai_sanitized}
                    aiStatusFinal={data.ai_status_final}
                    aiGeneratingTimedOut={!!data.ai_generating_timed_out}
                    ws7Metadata={data.ws7_metadata || null}
                  />
                </SectionErrorBoundary>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.18 }} className="lg:col-span-5 space-y-4">
                <SectionErrorBoundary section="key_drivers_panel" label="Key drivers" ticker={tk} isAdmin={!!(user?.is_admin || user?.role === "admin")}>
                  <KeyDriversPanel valuation={v} metrics={m} />
                </SectionErrorBoundary>
              </motion.div>
            </div>

            {/* Phase 8.LP.FREE-POLISH — Free Plan Usage as a wide,
                centered status card below the two-column section.
                Only rendered for Free users. Pro/Premium stay focused
                on the analysis (subscription management lives in the
                account menu, profile, and billing page). */}
            {(user?.plan || "free").toLowerCase() === "free" ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.20 }}
              >
                <UpgradePanel
                  usage={usage}
                  plan={user?.plan || "free"}
                  variant="horizontal"
                />
              </motion.div>
            ) : null}

            {/* Growth charts */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.22 }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GrowthChart title={t("analyze.growth_revenue")} data={data.growth?.revenue || []} color="#3182CE" testId="analyze-revenue-chart" />
              <GrowthChart title={t("analyze.growth_earnings")} data={data.growth?.earnings || []} color="#A8AEB7" testId="analyze-earnings-chart" />
            </motion.div>

            {/* KPI grid — basic metrics free, advanced metrics locked for free users.
                Each card receives both `value` (formatted string) AND `rawValue`
                (the underlying number or null). When `rawValue` is null/undefined,
                the card automatically renders "Data unavailable" instead of a
                misleading 0% / – fallback. (Phase 6.Y null-truthfulness rule.)

                Landing/Analysis Polish 1 — every KPI value passes through
                the centralized ``getKpiTone`` helper which returns
                "positive"|"neutral" based on canonical analysis
                evidence (sub_scores, classification, ai_score,
                valuation_unit_status). Positive tone renders the value
                in Stockaily green; neutral keeps the default color.
                No arbitrary large-number-is-positive heuristics. */}
            {(() => {
              const _kctx = {
                classification: v?.classification,
                ai_score: v?.ai_score,
                sub_scores: v?.sub_scores,
                valuation_unit_status: v?.valuation_unit_status,
              };
              const _acc = (metricKey, value) => kpiToneAccentClass(getKpiTone(metricKey, value, _kctx));
              const _sectorPercentileValue = v?.ai_score != null ? Math.max(0, Math.min(100, v.ai_score)) : null;
              const _fcfQualityRaw = v?.sub_scores?.quality ?? v?.sub_scores?.health ?? null;
              return (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.26 }} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="analyze-kpi-grid">
              <KpiCard label={t("analyze.kpi.market_cap")} value={fmtMoneyCompact(m.market_cap, v?.valuation_unit_status, m?.report_currency)} rawValue={m.market_cap} testId="kpi-market-cap" infoKey="market_cap" accent={_acc("market_cap", m.market_cap)} />
              <KpiCard label={t("analyze.kpi.pe")} value={fmtMultiplier(m.pe)} rawValue={m.pe} testId="kpi-pe" infoKey="pe" accent={_acc("pe", m.pe)} />
              <KpiCard label={t("analyze.kpi.forward_pe")} value={fmtMultiplier(m.forward_pe)} rawValue={m.forward_pe} testId="kpi-forward-pe" infoKey="forward_pe" accent={_acc("forward_pe", m.forward_pe)} />
              <KpiCard label={t("analyze.kpi.eps")} value={fmtMoney(m.eps, v?.valuation_unit_status, m?.report_currency)} rawValue={m.eps} testId="kpi-eps" infoKey="eps" accent={_acc("eps", m.eps)} />
              <KpiCard label={t("analyze.kpi.revenue")} value={fmtMoneyCompact(m.revenue_ttm, v?.valuation_unit_status, m?.report_currency)} rawValue={m.revenue_ttm} testId="kpi-revenue" infoKey="revenue" accent={_acc("revenue", m.revenue_ttm)} />
              <KpiCard label={t("analyze.kpi.revenue_growth")} value={m.revenue_growth_yoy != null ? fmtPercent(m.revenue_growth_yoy * 100) : null} rawValue={m.revenue_growth_yoy} testId="kpi-revenue-growth" infoKey="revenue_growth" accent={_acc("revenue_growth", m.revenue_growth_yoy)} />
              <KpiCard label={t("analyze.kpi.earnings_growth")} value={m.earnings_growth_yoy != null ? fmtPercent(m.earnings_growth_yoy * 100) : null} rawValue={m.earnings_growth_yoy} testId="kpi-earnings-growth" infoKey="earnings_growth" accent={_acc("earnings_growth", m.earnings_growth_yoy)} />
              <KpiCard label={t("analyze.kpi.profit_margin")} value={m.profit_margin != null ? fmtPercent(m.profit_margin * 100) : null} rawValue={m.profit_margin} testId="kpi-profit-margin" infoKey="profit_margin" accent={_acc("profit_margin", m.profit_margin)} />
              <KpiCard label={t("analyze.kpi.dividend_yield")} value={m.dividend_yield != null ? fmtPercent(m.dividend_yield * 100, 2) : null} rawValue={m.dividend_yield} testId="kpi-dividend-yield" infoKey="dividend_yield" accent={_acc("dividend_yield", m.dividend_yield)} />
              {/* Advanced — locked for free, full for Pro */}
              <KpiCard label={t("analyze.kpi.roic")} value={m.roic != null ? fmtPercent(m.roic * 100) : null} rawValue={m.roic} testId="kpi-roic" locked={!data.ai_full} lockedHint={t("analyze.locked_pro_cap_efficiency")} infoKey="roic" accent={_acc("roic", m.roic)} />
              <KpiCard
                label={t("analyze.kpi.fcf_quality")}
                value={(() => {
                  const q = v?.sub_scores?.quality;
                  const h = v?.sub_scores?.health;
                  const pick = q != null ? q : (h != null ? h : null);
                  if (pick == null || typeof pick !== "number" || Number.isNaN(pick)) return null;
                  return `${pick.toFixed(0)} / 100`;
                })()}
                rawValue={_fcfQualityRaw}
                testId="kpi-fcf-quality"
                locked={!data.ai_full}
                lockedHint={t("analyze.locked_pro_balance_sheet")}
                infoKey="fcf_quality"
                accent={_acc("fcf_quality", _fcfQualityRaw)}
              />
              <KpiCard label={t("analyze.kpi.sector_percentile")} value={v.ai_score != null ? `Top ${Math.max(5, 100 - v.ai_score)}%` : null} rawValue={_sectorPercentileValue} testId="kpi-sector-percentile" locked={!data.ai_full} lockedHint={t("analyze.locked_pro_sector_cohort")} infoKey="sector_percentile" accent={_acc("sector_percentile", _sectorPercentileValue)} />
            </motion.div>
              );
            })()}
          </div>
          </AnalyzeErrorBoundary>
        )}
        </>
          );
        })()}
      </main>
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} reason="You’ve hit your free analyses quota for this period." />
      {data?.ws7_metadata && (
        <div className="max-w-6xl mx-auto px-4 pb-6" data-testid="analyze-ws7-panel">
          <TransparencyPanel metadata={data.ws7_metadata} />
        </div>
      )}
      <Footer />
    </div>
  );
}



// ---------------------------------------------------------------------------
// Phase 6.AF — Admin-only diagnostic strip
// ---------------------------------------------------------------------------
// Shown above the analysis result for admin users only. Surfaces the new
// Phase 6.AF backend fields so the operator can verify at a glance:
//   * data_source_quality (mock / cached / partial_live / full_live)
//   * which FMP endpoints succeeded vs failed for THIS specific analyze
//   * the exact failure reason per endpoint (plan_restricted / rate_limited / etc)
//   * AI / LLM status + skip reason (ai_timeout / insufficient_evidence / etc)
//   * per-stage timing breakdown
// Hidden from non-admin users — they see the friendlier
// "Limited fundamental data" banner below this component instead.
function AdminAnalyzeDiagnostic({ data }) {
  if (!data) return null;
  const dsq = data.data_source_quality || "unknown";
  const ep = data.endpoints_status || { ok: [], failed: [], ok_count: 0, failed_count: 0 };
  const stages = data.stages || {};
  const dc = data?.metrics?.data_completeness;
  const ai = data.ai_summary_status || "unknown";
  const llmSkipped = data.llm_skipped_reason;
  const aiFail = data.ai_fail_reason;
  const narrative = data.narrative_status || "skipped";
  // Phase 6.AG — _meta_debug carries schema-validity + render-safety
  // diagnostics from the backend. Always shown to admins so they can
  // immediately see whether the response is "frontend-renderable" and
  // how many fields are null vs populated.
  const meta = data._meta_debug || {};
  // Color-code data_source_quality so the badge instantly communicates
  // whether the analysis is live or stale/mocked.
  const dsqColor = (
    dsq === "full_live" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700" :
    dsq === "partial_live" ? "bg-amber-500/15 border-amber-500/40 text-amber-700" :
    dsq === "cached" ? "bg-blue-500/15 border-blue-500/40 text-blue-700" :
    dsq === "mock" ? "bg-rose-500/15 border-rose-500/40 text-rose-700" :
    "bg-slate-500/15 border-slate-500/40 text-slate-700"
  );
  const aiColor = (
    ai === "available" ? "text-emerald-700" :
    ai === "fallback_template" ? "text-amber-700" :
    "text-rose-700"
  );
  return (
    <div
      className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-3 space-y-3"
      data-testid="admin-analyze-diagnostic"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">
          Admin diagnostic
        </span>
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${dsqColor}`}
          data-testid="admin-diag-data-source-quality"
        >
          {dsq.replace("_", " ")}
        </span>
        <span className="text-[10px] text-muted-foreground">
          data_completeness:{" "}
          <code className={`font-mono ${dc != null && dc <= 0.2 ? "text-rose-700" : ""}`}>
            {dc != null ? `${(dc * 100).toFixed(0)}%` : "?"}
          </code>
        </span>
        <span className="text-[10px] text-muted-foreground">
          AI:{" "}
          <code className={`font-mono ${aiColor}`} data-testid="admin-diag-ai-status">
            {ai}{llmSkipped ? ` (${llmSkipped})` : ""}
            {aiFail ? ` · ${aiFail}` : ""}
          </code>
        </span>
        {narrative && narrative !== "skipped" ? (
          <span className="text-[10px] text-muted-foreground">
            narrative: <code className="font-mono">{narrative}</code>
          </span>
        ) : null}
      </div>
      {/* FMP endpoint outcomes — grid of pills */}
      <div data-testid="admin-diag-endpoints">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
          FMP endpoints for this analyze
        </div>
        {ep.ok_count === 0 && ep.failed_count === 0 ? (
          <div className="text-[11px] text-muted-foreground">
            No per-endpoint detail attached (likely served from raw FMP cache).
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(ep.ok || []).map((name) => (
              <span
                key={`ok-${name}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-700"
                data-testid={`admin-diag-ep-ok-${name}`}
              >
                <CheckCircle2 className="w-3 h-3" />
                {name}
              </span>
            ))}
            {(ep.failed || []).map((f) => (
              <span
                key={`f-${f.key || f.path}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-700"
                data-testid={`admin-diag-ep-fail-${f.key || f.path}`}
                title={`${f.path} → ${f.status || "?"} ${f.reason || ""}`}
              >
                <XCircle className="w-3 h-3" />
                {f.key || f.path}
                <code className="text-[10px] ml-0.5 opacity-80">
                  {f.status || "?"} · {f.reason || "fail"}
                </code>
              </span>
            ))}
          </div>
        )}
      </div>
      {/* Per-stage timings */}
      {stages && Object.keys(stages).length ? (
        <div className="text-[10px] text-muted-foreground" data-testid="admin-diag-stages">
          <span className="uppercase tracking-wide mr-2">stages:</span>
          {Object.entries(stages).map(([k, v]) => (
            <span key={k} className="mr-3">
              <code className="font-mono">{k.replace("_ms", "")}</code>{" "}
              <span className={v > 10000 ? "text-amber-700" : ""}>{v}ms</span>
            </span>
          ))}
        </div>
      ) : null}
      {/* Plan_restricted hint */}
      {(ep.failed || []).some((f) => f.reason === "plan_restricted") ? (
        <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid="admin-diag-plan-hint">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            One or more FMP endpoints returned <strong>402 plan_restricted</strong>.
            These endpoints are not available on the current FMP plan. The
            analysis continued with the endpoints that DID succeed (see green
            pills above). Upgrade the FMP plan or accept the partial coverage.
          </div>
        </div>
      ) : null}

      {/* Phase 6.AG — Schema & render-safety bar */}
      {meta && Object.keys(meta).length ? (
        <div className="pt-2 mt-2 border-t border-violet-500/20" data-testid="admin-diag-meta">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Response contract
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-[11px]">
            <div>
              <span className="text-muted-foreground">schema_valid:</span>{" "}
              <code className={`font-mono ${meta.schema_valid ? "text-emerald-700" : "text-rose-700"}`} data-testid="admin-diag-schema-valid">
                {String(meta.schema_valid)}
              </code>
            </div>
            <div>
              <span className="text-muted-foreground">render_safe:</span>{" "}
              <code className={`font-mono ${meta.frontend_render_safe ? "text-emerald-700" : "text-rose-700"}`} data-testid="admin-diag-render-safe">
                {String(meta.frontend_render_safe)}
              </code>
            </div>
            <div>
              <span className="text-muted-foreground">null_fields:</span>{" "}
              <code className={`font-mono ${(meta.null_ratio_pct || 0) > 50 ? "text-amber-700" : ""}`} data-testid="admin-diag-null-count">
                {meta.null_fields_count}/{meta.total_fields_count} ({meta.null_ratio_pct}%)
              </code>
            </div>
            <div>
              <span className="text-muted-foreground">request_id:</span>{" "}
              <code className="font-mono text-amber-700" data-testid="admin-diag-request-id">
                {meta.request_id || "n/a"}
              </code>
            </div>
          </div>
          {Array.isArray(meta.schema_safety_reasons) && meta.schema_safety_reasons.length ? (
            <div className="mt-1 text-[10px] text-rose-700">
              <span className="text-muted-foreground">unsafe reasons:</span>{" "}
              {meta.schema_safety_reasons.join(", ")}
            </div>
          ) : null}
          {Array.isArray(meta.unavailable_endpoints) && meta.unavailable_endpoints.length ? (
            <div className="mt-1 text-[10px] text-amber-700">
              <span className="text-muted-foreground">unavailable endpoints:</span>{" "}
              <code className="font-mono">{meta.unavailable_endpoints.join(", ")}</code>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
